import { PaymentMethodSettings } from "./PaymentMethodSettings";
import { Button, GridLegacy as Grid } from "@mui/material";
import React, { Fragment, useContext, useEffect, useState } from "react";

import NotchedCard from "../../components/cards/NotchedCard/NotchedCard";
import EditableField, { EditableFieldTypes } from "../../components/editable/EditableField/EditableField";
import DistributionListField from "../../components/editable/EditableField/fields/DistributionList/DistributionListField";
import { IDropDownListItem } from "../../components/editable/EditableField/fields/DropDownField";
import EditableGroup, { EditableGroupContext, EditableGroupType } from "../../components/editable/EditableGroup/EditableGroup";
import PrinterLabelEditor from "../../components/labels/labelPritner/printerLabelEditor";
import LabelPrinterSetting from "../../components/labels/labelPritner/printerSettings";
import TabbedView from "../../components/lists/TabbedView/TabbedView";
import TabbedViewPage from "../../components/lists/TabbedView/TabbedViewPage";
import PackageManager from "../../components/packages/manager/PackageManager";
import ProductManager from "../../components/products/ProductManager";
import UpsellsManager from "../../components/upsells/manager/UpsellsManager";
import UserManagement from "../../components/users/UserManager";
import { api, AutoCancellationTimeUnit, BookingDurationTimeUnit, Category, DistanceUnit, DynamicAllocationTier, IFlightLookupFieldOptions, IPreferences, LaneAllocationSystem, ShuttleBusPickupTrigger, SystemProfile, VehicleIssueCategory, VehicleIssueType } from "../../logic/api";
import { ReleaseFlag, useReleaseManagement } from "../../utility/feature-management";
import { retrieveValue, storeObject, storeValue } from "../../utility/storage";
import { SessionContext } from "../../utility/useSession";
import { formatCamel, hasKeyMasterPermissions, isAdmin, isSysAdmin } from "../../utility/utils";
import DashPage from '../dash/DashPage';
import cssClasses from "./SettingsPage.module.css";
import styles from "./styles";

import { useTranslation } from "react-i18next";
import { FiGitBranch, FiTrash } from "react-icons/fi";
import { getCountries } from "react-phone-number-input";
import UserSettingsManager from "../../components/users/UserSettingsManager";
import WebhooksManager from "../../components/webhooks/WebhooksManager";
import { usePreferences } from "../../utility/usePreferences";
import IngestionView from "../admin/IngestionView";
import { KmosTimeSlotEditor } from "../kmos/v1.0/components/KmosTimeSlotEditor";
import LaneAllocationView from "../kmos/v1.0/components/LaneAllocationView";
import DeviceManagerView from "../mobile/devices_admin/DeviceManagerView";
import { BusManager } from "../walkarounds/wa_bus/BusManager";
import BarrierTokenPrinter from "./BarrierTokenPrinter";
import { BookingRecordSettings } from "./BookingRecordSettings";
import { BookingScanOptionSettings } from "./BookingScanOptions";
import { BusWalkAroundSettings } from "./BusWalkaroundSettings";
import { CommunicationSettings } from "./CommunicationSettings";
import { ContactRecordSettings } from './ContactRecordSettings';
import { CustomerLookupSettings } from './CustomerLookupSettings';
import { MobileVehicleMovementSettings } from "./MobileVehicleMovementSettings";
import { ProductCatalogueSetting } from './product-catalogue-setting';
import { ReturnFlightRecordSettings } from './ReturnFlightRecordSettings';
import { OutboundFlightRecordSettings } from './OutboundFlightRecordSettings';
import { TasksSection } from "./TasksSection";
import { VehicleRecordSettings } from './VehicleRecordSettings';
import { WorkflowRuleSettings } from "./WorkflowRuleSettings";
import { BrandManagementSettings } from "../../components/brand/BrandManagementSettings";
import { BrandSettings } from "./BrandSettings";
import { ScanningPageSettings } from "./ScanningPageSettings";
import { DriverShiftTrackerSettings } from "./DriverShiftTrackerSettings";
import { MeetAndGreetSettings } from "./MeetAndGreetSettings";
import { ShuttlePickupSettings } from "./components/shuttlePickup/ShuttlePickupSettings";
import { NoticePeriodSettings } from "./components/noticePeriod/NoticePeriodSettings";
import { GenerateRecommendationsButton } from "./GenerateRecommendationsButton";
import { useUserPermissions } from "@/hooks/useUserPermissions";
import { KeyAuditSettings } from "./KeyAuditSettings";
import { KeyAuditSettingsV2 } from "./KeyAuditSettingsV2";
import { DriverAvailabilitySettings } from "@/pages/settings/components/driverAvailability/DriverAvailabilitySettings";
import { BookingsPageSettings } from "./BookingsPageSettings";

export const SettingsPage = () => {
  const session = useContext(SessionContext);
  const updatePrefsApi = api.hooks.preferences.update();
  const vehicleIssueTypesApi = api.hooks.vehicleManager.getVehicleIssueTypes();
  const { t } = useTranslation()
  const { hasPermission } = useUserPermissions();

  const deprecateArchiveReleased = useReleaseManagement(ReleaseFlag.releaseDeprecateArchiveStatus);
  const manageVehicleIssueTypesReleased = useReleaseManagement(ReleaseFlag.manageVehicleIssueTypes);
  const releasePrintBookingDetailsSetting = useReleaseManagement(ReleaseFlag.releasePrintBookingDetailsSetting);
  const bookingScanOptionsReleased = useReleaseManagement(ReleaseFlag.releaseBookingScanOptions);
  const kmosV2Released = useReleaseManagement(ReleaseFlag.kmosV2);
  const additionalTaxRatesReleased = useReleaseManagement(ReleaseFlag.releaseAdditionalTaxRates);
  const distanceSettingReleased = useReleaseManagement(ReleaseFlag.releaseDistanceSetting);
  const digitalCollectionReceiptReleased = useReleaseManagement(ReleaseFlag.digitalCollectionReceipt);
  const kmosManualAllocationReleased = useReleaseManagement(ReleaseFlag.kmosManualAllocationReleased);
  const releaseKeyAudit6342 = useReleaseManagement(ReleaseFlag.releaseKeyPositionAudit6342);
  const isMoveTimeSlotManagerReleased = useReleaseManagement(ReleaseFlag.moveKmosTimeSlotsSettings);
  const isKeyMasterRoleReleased = useReleaseManagement(ReleaseFlag.releaseKeyMasterRole);
  const deLinkTaxNameAndTaxNumber = useReleaseManagement(ReleaseFlag.releaseDeLinkCustomerTaxNameAndTaxNumber);
  const isUndoCheckInReleased = useReleaseManagement(ReleaseFlag.releaseUndoCheckIn);
  const isSydneyDualParkAllocationReleased = useReleaseManagement(ReleaseFlag.releaseSydneyDualParkAllocation);
  const isPairedJourneysReleased = useReleaseManagement(ReleaseFlag.releasePairedJourneys);
  const isDynamicAllocationPreferenceReleased = useReleaseManagement(ReleaseFlag.releaseDynamicAllocationPreference);
  const isRbacReleased = useReleaseManagement(ReleaseFlag.releaseRbac);
  const isAutoCancellationCalendarDaysReleased = useReleaseManagement(ReleaseFlag.releaseAutoCancellationCalendarDays);
  const isTenantCopyReleased = useReleaseManagement(ReleaseFlag.tenantDataCopy);
  const isSessionInactivityTimeoutDurationReleased = useReleaseManagement(ReleaseFlag.releaseSessionInactivityTimeoutDuration);
  const isBrandNameReleased = useReleaseManagement(ReleaseFlag.releaseBrandName);
  const isExternalIdReferencesReleased = useReleaseManagement(ReleaseFlag.releaseAdditionalExternalReferences);
  const isKeyAuditDueCriteriaReleased = useReleaseManagement(ReleaseFlag.releaseKeyAuditDueCriteria);
  const isKeyAuditDueCriteriaTextReleased = useReleaseManagement(ReleaseFlag.releaseKeyAuditDueCriteriaText);
  const isTrackingIdValidationReleased = useReleaseManagement(ReleaseFlag.releaseTrackingIdValidation);
  const isWebhookSettingsForSystemAdminsReleased = useReleaseManagement(ReleaseFlag.releaseWebhookSettingsForSystemAdmins);
  const isDriverShiftTrackerReleased = useReleaseManagement(ReleaseFlag.releaseDriverShiftTracker);
  const isBookingLastSeenDateReleased = useReleaseManagement(ReleaseFlag.releaseBookingLastSeenDate);
  const isFlightRecordOptionsReleased = useReleaseManagement(ReleaseFlag.releaseFlightRecordOptions);
  const isRenameOutboundReturnReleased = useReleaseManagement(ReleaseFlag.releaseRenameOutboundReturnFlightSettings);
  const isScanningPageReleased = useReleaseManagement(ReleaseFlag.releaseScanningPage);
  const isShuttlePickupReleased = useReleaseManagement(ReleaseFlag.releaseShuttlePickup);
  const isShuttlePickupFieldsAndMappingsReleased = useReleaseManagement(ReleaseFlag.releaseShuttlePickupFieldsAndMappings);
  const isPickupLocationReleased = useReleaseManagement(ReleaseFlag.releasePickupLocation);
  const isWhat3wordsToSettingsReleased = useReleaseManagement(ReleaseFlag.releaseWhat3wordsToSettings);
  const isAddNoticePeriodToSettingsReleased = useReleaseManagement(ReleaseFlag.releaseAddNoticePeriodToSettings);
  const isKioskLabelsReleased = useReleaseManagement(ReleaseFlag.releaseKioskLabels);
  const isRbacLanePlanningReleased = useReleaseManagement(ReleaseFlag.releaseRbacLanePlanningPermissions);
  const isDefaultBookingDurationReleased = useReleaseManagement(ReleaseFlag.releaseDefaultBookingDuration);
  const isImproveContactRecordOptionsReleased = useReleaseManagement(ReleaseFlag.releaseImproveContactRecordOptions);
  const isMeetAndGreetFeaturesSettingsReleased = useReleaseManagement(ReleaseFlag.releaseMeetAndGreetFeaturesSettings);
  const isDisplayCleanAirComplianceReleased = useReleaseManagement(ReleaseFlag.releaseDisplayCleanAirCompliance);
  const isScheduledFlightValidatorReleased = useReleaseManagement(ReleaseFlag.releaseScheduledFlightValidator);
  const isLiveFlightDataReleased = useReleaseManagement(ReleaseFlag.releaseLiveFlightData);

  const shouldRenderPaymentMethodSetting = (isAdmin(session) || isSysAdmin(session));
  const canViewWebhooks = isWebhookSettingsForSystemAdminsReleased ? isSysAdmin(session) : isAdmin(session);

  const { isPackageManagementSupported, isProductManagementSupported, isUpsellsManagementSupported } = usePreferences();
  const canManagePackages = isPackageManagementSupported();
  const canManageProducts = isProductManagementSupported();
  const canManageUpsells = isUpsellsManagementSupported();
  interface VehicleIssueTypeViewModel extends VehicleIssueCategory {
    issueTypes: VehicleIssueType[];
  }
  const [vehicleIssueTypes, setVehicleIssueTypes] = useState<VehicleIssueTypeViewModel[]>([]);
  const [showTimeSlotEditor, setShowTimeSlotEditor] = useState(false);
  const [showLaneAllocationRulesView, setShowLaneAllocationRulesView] = useState(false);
  const [selectedAllocationSystem, setSelectedAllocationSystem] = useState<LaneAllocationSystem>(
    session.current.site?.preferences.lane_allocation_system ?? LaneAllocationSystem.None
  );
  const [shuttlePickupEnabled, setShuttlePickupEnabled] = useState<boolean>(
    session.current.site?.preferences?.request_shuttle_pickup_enabled ?? false
  );
  const showShuttlePickupFields = isShuttlePickupFieldsAndMappingsReleased && isShuttlePickupReleased && shuttlePickupEnabled;
  const showPickupLocationManagement = isPickupLocationReleased && shuttlePickupEnabled;
  const showShuttlePickupCard = showShuttlePickupFields || showPickupLocationManagement;

  const updateTimeSlotEditor = () => {
    setShowTimeSlotEditor(false);
  };

  interface TaxRate {
    name?: string,
    rate?: number,
  }

  const initialTaxRates: React.SetStateAction<TaxRate[]> = [];

  //in the long term, want to be able to make a change to have as many tax rates as needed. Once the db table structure is changed, we should be able to map to this array and then map back when updating the preferences.
  if (session.current.site?.preferences.vat_name && session.current.site?.preferences.vat_rate) {
    initialTaxRates.push({ name: session.current.site.preferences.vat_name, rate: session.current.site.preferences.vat_rate });
  }

  if (session.current.site?.preferences.vat_name_2 && session.current.site?.preferences.vat_rate_2) {
    initialTaxRates.push({ name: session.current.site.preferences.vat_name_2, rate: session.current.site.preferences.vat_rate_2 });
  }

  const [taxRates, setTaxRates] = useState<TaxRate[]>(initialTaxRates);

  useEffect(() => {
    vehicleIssueTypesApi.send(
      session.current.context,
      { includeInactive: true },
      (value) => {
        if (!value) {
          return;
        }

        const vehicleIssueCategories = value.reduce((accumulator, issueType) => {
          const category = issueType.category;

          if (!accumulator[category.id]) {
            accumulator[category.id] = { ...category, issueTypes: [] };
          }
          accumulator[category.id].issueTypes.push(issueType);

          return accumulator;
        }, [] as VehicleIssueTypeViewModel[]);

        const sortedVehicleIssueCategories = vehicleIssueCategories.map((category) => {
          category.issueTypes = category.issueTypes.sort((a, b) => a.name.localeCompare(b.name));
          return category;
        }).filter(category => category.name !== "locationIssues");

        setVehicleIssueTypes(sortedVehicleIssueCategories);
      })
  }, []);

  //this function will eventually be used to map the tax rates array to the preferences tax rates when it is able to support multiple tax rates
  const mapTaxRates = (preferences: Partial<IPreferences>): Partial<IPreferences> => {
    const newPreferences: Partial<IPreferences> = { ...preferences };

    if (taxRates[0].name && taxRates[0].rate &&
      "vat_rate" in newPreferences && "vat_name" in newPreferences
    ) {
      newPreferences.vat_name = taxRates[0].name;
      newPreferences.vat_rate = taxRates[0].rate;
    }

    if ("vat_rate_2" in newPreferences && "vat_name_2" in newPreferences) {
      if (taxRates[1]) {
        newPreferences.vat_name_2 = taxRates[1].name;
        newPreferences.vat_rate_2 = taxRates[1].rate;
      }
      else if (!taxRates[1]) {
        newPreferences.vat_name_2 = undefined;
        newPreferences.vat_rate_2 = undefined;
      }
    }

    return newPreferences;
  }

  const normaliseDefaultBookingDuration = (preferences: IPreferences): IPreferences["defaultBookingDuration"] => {
    const duration = preferences.defaultBookingDuration;

    if (duration == null) {
      return null;
    }

    return {
      enabled: duration.enabled === true,
      quantity: Number.isInteger(duration.quantity) && duration.quantity > 0 ? duration.quantity : 1,
      type: BookingDurationTimeUnit.Days,
    };
  };

  const savePrefs = async (newPrefs: Partial<IPreferences>) => {
    const mappedPrefs = additionalTaxRatesReleased ? mapTaxRates(newPrefs) : { ...newPrefs };
    return new Promise<boolean>((accept) => {
      updatePrefsApi.send(session.current.context, mappedPrefs, (updatedPrefs) => {
        if (updatedPrefs != null) {
          session.setPreferences(updatedPrefs);
          accept(true);
        } else {
          accept(false);
        }
      });
    })
  }

  const ensureFlightLookupFieldOptions = (data: IPreferences): IFlightLookupFieldOptions => {
    if (!data.flightLookupFieldOptions?.properties) {
      data.flightLookupFieldOptions = { properties: {} };
    }
    return data.flightLookupFieldOptions;
  };

  const addTaxRate = () => {
    setTaxRates(prevState => [...prevState, { name: undefined, rate: undefined }]);
  }

  const removeTaxRateRow = (index: number) => {
    setTaxRates(prevState => prevState.splice(index - 1, 1));
  }

  const autoCancellationTimeUnitMap: Record<AutoCancellationTimeUnit, string> = {
    [AutoCancellationTimeUnit.Hours]: "general.hours",
    [AutoCancellationTimeUnit.CalendarDays]: "general.calendarDays",
  };

  const getAutoCancellationTimeUnitOptions = (): IDropDownListItem[] => {
    return Object.entries(autoCancellationTimeUnitMap).map(([value, textKey]) => ({
      name: t(textKey),
      value: value
    }));
  };

  const autoCancellationTimeUnitQuantityLabel = () => {
    switch (session.current.site?.preferences.auto_archive_cancellation_time_unit) {
      case AutoCancellationTimeUnit.Hours:
        return `${t("general.hours")} ${t("settings.afterBookingStart")}`;
      case AutoCancellationTimeUnit.CalendarDays:
        return `${t("general.calendarDays")} ${t("settings.afterBookingStart")}`;
      default:
        return `${t("general.hours")} ${t("settings.afterBookingStart")}`;
    }
  }

  const getLaneAllocationOptions = (): IDropDownListItem[] => {
    return Object.entries(laneAllocationMap).map(([value, textKey]) => ({
      name: t(textKey),
      value: value
    }));
  }

  // @ts-expect-error For not providing the LaneAllocationSystem.DAS option
  const laneAllocationMap: Record<LaneAllocationSystem, string> = {
    [LaneAllocationSystem.None]: "general.none",
    [LaneAllocationSystem.KMOS]: "laneAllocationSystem.labels.kmos",
    [LaneAllocationSystem.DualPark]: "laneAllocationSystem.labels.dualPark",
  };
  if (isDynamicAllocationPreferenceReleased) {
    laneAllocationMap[LaneAllocationSystem.DAS] = "laneAllocationSystem.labels.das"
  }

  const DYNAMIC_ALLOCATION_TIER_LABELS = new Map([
    [DynamicAllocationTier.Basic, t("settings.dynamicAllocationTierBasic")],
    [DynamicAllocationTier.Disabled, t("settings.dynamicAllocationTierDisabled")],
    [DynamicAllocationTier.Full, t("settings.dynamicAllocationTierFull")],
  ])

  if (session.current.site == null) {
    return <Fragment />
  }

  const canUpdateKmosRules = isRbacLanePlanningReleased ? hasPermission("lanePlanning.rule.update") : hasKeyMasterPermissions(session);


  return (<DashPage>
    <Fragment>

      <div style={{ padding: 20 }}>
        <div style={{ textAlign: "center" }}>
          <div style={{ padding: 20, marginBottom: 50 }}>
            <div><span style={styles.title}>{t("settings.systemSettings")}</span></div>
            <div><span style={styles.message}>{t("settings.configOperatorPortal")}</span></div>
          </div>
          <div style={{ width: "100%", maxWidth: 1200, display: "inline-block" }}>
            <TabbedView selectedIndex={1}>

              <TabbedViewPage visible={isSysAdmin(session)} title="Features">
                <Grid container>
                  <Grid item xs={12}>
                    <NotchedCard label={t("settings.system")} style={styles.card}>
                      <EditableGroup<IPreferences>
                        id="bookings"
                        testId="settings-system-group"
                        editType={EditableGroupType.switchableEditing}
                        data={session.current.site.preferences}
                        onDataChanged={(data) => savePrefs({
                          system_profile: data.system_profile,
                        })}
                      >
                        <Grid container>
                          <EditableField<IPreferences, IDropDownListItem>
                            testId="settings-system-profile-field"
                            inputType={EditableFieldTypes.enumDropDownField}
                            label={t("settings.profile")}

                            onProvide={(data) => {
                              return {
                                name: "",
                                value:
                                  data.system_profile == null
                                    ? SystemProfile.airportParking.toString()
                                    : data.system_profile.toString(),
                              };
                            }}
                            onRequestText={(data) => {
                              if (data.system_profile == null) return "";
                              return formatCamel(SystemProfile[data.system_profile]);
                            }}
                            onChange={(data, value) => {
                              data.system_profile = parseInt(value.value ?? "");
                            }}
                            onRequestData={async () => {
                              const items: IDropDownListItem[] = [];
                              for (const pt in SystemProfile) {
                                if (isNaN(parseInt(pt))) {
                                  items.push({
                                    name: formatCamel(pt.toString()),
                                    value: SystemProfile[pt],
                                  });
                                }
                              }
                              return items;
                            }}
                          />
                        </Grid>
                      </EditableGroup>
                    </NotchedCard>
                  </Grid>

                  <Grid item xs={12}>
                    <NotchedCard
                      label={t("settings.bookings")}
                      style={styles.card}
                      containerProps={{ 'data-testid': 'booking-features' }}
                    >
                      <EditableGroup<IPreferences>
                        id="bookings"
                        testId="settings-booking-features-group"
                        editType={EditableGroupType.switchableEditing}
                        data={session.current.site.preferences}
                        onDataChanged={(data) => savePrefs({
                          booking_create_enabled: data.booking_create_enabled,
                          booking_update_enabled: data.booking_update_enabled,
                          booking_packages_enabled: data.booking_packages_enabled,
                          booking_products_enabled: data.booking_products_enabled,
                          booking_payments_enabled: data.booking_payments_enabled,
                          booking_remarks_always_enabled: data.booking_remarks_always_enabled,
                          booking_print_details_enabled: data.booking_print_details_enabled,
                          express_checkout_enabled: data.express_checkout_enabled,
                          lockBookingEndTime: data.lockBookingEndTime
                        })}
                      >
                        <Grid container>
                          <EditableField<IPreferences, boolean>
                            testId="settings-create-bookings-switch"
                            inputType={EditableFieldTypes.switch}
                            label={t("settings.createBookings")}
                            onProvide={(data) => data.booking_create_enabled}
                            onChange={(data, value) => data.booking_create_enabled = value}
                          />
                          <EditableField<IPreferences, boolean>
                            testId="settings-update-bookings-switch"
                            inputType={EditableFieldTypes.switch}
                            label={t("settings.updateBookings")}
                            onProvide={(data) => data.booking_update_enabled}
                            onChange={(data, value) => data.booking_update_enabled = value}
                          />
                          <EditableField<IPreferences, boolean>
                            testId="settings-booking-packages-switch"
                            inputType={EditableFieldTypes.switch}
                            label={t("kmos.labels.packages")}
                            onProvide={(data) => data.booking_packages_enabled}
                            onChange={(data, value) => data.booking_packages_enabled = value}
                          />
                          <EditableField<IPreferences, boolean>
                            testId="settings-booking-products-switch"
                            inputType={EditableFieldTypes.switch}
                            label={t("booking.labels.products")}
                            onProvide={(data) => data.booking_products_enabled}
                            onChange={(data, value) => data.booking_products_enabled = value}
                          />
                          <EditableField<IPreferences, boolean>
                            testId="settings-booking-payments-switch"
                            inputType={EditableFieldTypes.switch}
                            label={t("booking.labels.payments")}
                            onProvide={(data) => data.booking_payments_enabled}
                            onChange={(data, value) => data.booking_payments_enabled = value}
                          />
                          <EditableField<IPreferences, boolean>
                            testId="settings-remarks-always-available-switch"
                            inputType={EditableFieldTypes.switch}
                            label={t("settings.remarksAlwaysAvailable")}
                            onProvide={(data) => data.booking_remarks_always_enabled}
                            onChange={(data, value) => data.booking_remarks_always_enabled = value}
                          />
                          <EditableField<IPreferences, boolean>
                            testId="settings-express-checkout-switch"
                            inputType={EditableFieldTypes.switch}
                            label={t("expressCheckout.title")}
                            onProvide={(data) => data.express_checkout_enabled}
                            onChange={(data, value) => data.express_checkout_enabled = value}
                          />
                          <EditableField<IPreferences, boolean>
                            testId="settings-lock-booking-end-switch"
                            inputType={EditableFieldTypes.switch}
                            label={t("settings.lockBookingEnd")}
                            onProvide={(data) => data.lockBookingEndTime || false}
                            onChange={(data, value) => data.lockBookingEndTime = value}
                          />
                          {releasePrintBookingDetailsSetting && (
                            <EditableField<IPreferences, boolean>
                              testId="settings-print-booking-details-switch"
                              inputType={EditableFieldTypes.switch}
                              label={t("settings.printBookingDetails")}
                              onProvide={(data) => data.booking_print_details_enabled ?? true}
                              onChange={(data, value) => data.booking_print_details_enabled = value}
                            />
                          )}
                        </Grid>
                      </EditableGroup>
                    </NotchedCard>
                  </Grid>

                  <BookingsPageSettings
                    preferences={session.current.site.preferences}
                    onSave={savePrefs}
                  />

                  <WorkflowRuleSettings onSave={(data) => savePrefs(data)} />

                  {!isImproveContactRecordOptionsReleased && (
                    <Grid item xs={12}>
                      <NotchedCard
                        label={t("settings.bookingFields")}
                        style={styles.card}
                      >
                        <EditableGroup<IPreferences>
                          id="booking"
                          testId="settings-booking-fields-group"
                          editType={EditableGroupType.switchableEditing}
                          data={session.current.site.preferences}
                          onDataChanged={(data) =>
                            savePrefs({
                              booking_leadContact_title_enabled:
                                data.booking_leadContact_title_enabled,
                              booking_leadContact_title_dropdown:
                                data.booking_leadContact_title_dropdown,
                              booking_leadContact_firstName_enabled:
                                data.booking_leadContact_firstName_enabled,
                            })
                          }
                        >
                          <Grid container>
                            <EditableField<IPreferences, boolean>
                              testId="settings-booking-title-switch"
                              inputType={EditableFieldTypes.switch}
                              label={t("booking.labels.title")}
                              onProvide={(data) =>
                                data.booking_leadContact_title_enabled
                              }
                              onChange={(data, value) => {
                                data.booking_leadContact_title_enabled = value;
                              }}
                            />

                            <EditableField<IPreferences, boolean>
                              testId="settings-title-dropdown-switch"
                              inputType={EditableFieldTypes.switch}
                              label={t("settings.titleDropdown")}
                              onProvide={(data) =>
                                data.booking_leadContact_title_dropdown
                              }
                              onChange={(data, value) => {
                                data.booking_leadContact_title_dropdown = value;

                                if (value) {
                                  data.booking_leadContact_title_enabled = true;
                                }
                              }}
                            />

                            <EditableField<IPreferences, boolean>
                              testId="settings-first-name-initial-switch"
                              inputType={EditableFieldTypes.switch}
                              label={t("settings.firstNameInitial")}
                              onProvide={(data) =>
                                data.booking_leadContact_firstName_enabled
                              }
                              onChange={(data, value) => {
                                data.booking_leadContact_firstName_enabled = value;
                              }}
                            />
                          </Grid>
                        </EditableGroup>
                      </NotchedCard>
                    </Grid>
                  )}

                  <Grid item xs={12}>
                    <NotchedCard label={t("settings.upsells")} style={styles.card}>
                      <EditableGroup<IPreferences>
                        id="upsells"
                        testId="settings-upsells-group"
                        editType={EditableGroupType.switchableEditing}
                        data={session.current.site.preferences}
                        onDataChanged={(data) => savePrefs({
                          booking_upsells_enabled: data.booking_upsells_enabled,
                          upsell_kiosk_img_w: data.upsell_kiosk_img_w,
                          upsell_mobile_img_w: data.upsell_mobile_img_w,
                          upsell_kiosk_img_h: data.upsell_kiosk_img_h,
                          upsell_mobile_img_h: data.upsell_mobile_img_h,
                        })}
                      >
                        <Grid container>
                          <EditableField<IPreferences, boolean>
                            testId="settings-upsells-enabled-switch"
                            inputType={EditableFieldTypes.switch}
                            label={t("reports.enabled")}
                            onProvide={(data) => data.booking_upsells_enabled}
                            onChange={(data, value) =>
                              (data.booking_upsells_enabled = value)
                            }
                          />
                          <EditableField<IPreferences, string>
                            inputType={EditableFieldTypes.empty}
                            label={""}
                          />
                          <EditableField<IPreferences, number>
                            testId="settings-kiosk-image-width-field"
                            inputType={EditableFieldTypes.number}
                            label={t("settings.kioskImageWidth")}
                            onProvide={(data) =>
                              data.upsell_kiosk_img_w ?? 600
                            }
                            onChange={(data, value) => {
                              if (value > 0) {
                                data.upsell_kiosk_img_w = value;
                              }
                            }}
                          />

                          <EditableField<IPreferences, number>
                            testId="settings-mobile-image-width-field"
                            inputType={EditableFieldTypes.number}
                            label={t("settings.mobileImageWidth")}
                            onProvide={(data) =>
                              data.upsell_mobile_img_w ?? 400
                            }
                            onChange={(data, value) => {
                              if (value > 0) {
                                data.upsell_mobile_img_w = value;
                              }
                            }}
                          />

                          <EditableField<IPreferences, number>
                            testId="settings-kiosk-image-height-field"
                            inputType={EditableFieldTypes.number}
                            label={t("settings.kioskImageHeight")}
                            onProvide={(data) =>
                              data.upsell_kiosk_img_h ?? 800
                            }
                            onChange={(data, value) => {
                              if (value > 0) {
                                data.upsell_kiosk_img_h = value;
                              }
                            }}
                          />

                          <EditableField<IPreferences, number>
                            testId="settings-mobile-image-height-field"
                            inputType={EditableFieldTypes.number}
                            label={t("settings.mobileImageHeight")}
                            onProvide={(data) =>
                              data.upsell_mobile_img_h ?? 600
                            }
                            onChange={(data, value) => {
                              if (value > 0) {
                                data.upsell_mobile_img_h = value;
                              }
                            }}
                          />
                        </Grid>
                      </EditableGroup>
                    </NotchedCard>
                  </Grid>

                  <CustomerLookupSettings onSave={savePrefs} />

                  <MobileVehicleMovementSettings onSave={savePrefs} />

                  <Grid item xs={12}>
                    <NotchedCard label={t("settings.wmb")} style={styles.card}>
                      <EditableGroup<IPreferences>
                        id="wmb"
                        testId="settings-wmb-group"
                        editType={EditableGroupType.switchableEditing}
                        data={session.current.site.preferences}
                        onDataChanged={(data) => savePrefs({
                          wmb_enabled: data.wmb_enabled,
                          request_shuttle_pickup_enabled: data.request_shuttle_pickup_enabled,
                          pickup_trigger: data.pickup_trigger,
                        })}
                        onCancelEdit={() => setShuttlePickupEnabled(session.current.site?.preferences?.request_shuttle_pickup_enabled ?? false)}
                      >
                        <Grid container>
                          <EditableField<IPreferences, boolean>
                            testId="settings-wmb-enabled-switch"
                            columnWidth={12} // Added this to force the next two fields on to their own rows
                            inputType={EditableFieldTypes.switch}
                            label={t("wmb.enabled")}
                            onProvide={(data) => data.wmb_enabled == true}
                            onChange={(data, value) =>
                              (data.wmb_enabled = value)
                            }
                          />
                          {isShuttlePickupReleased && (
                            <>
                              <EditableField<IPreferences, boolean>
                                testId="settings-wmb-shuttle-pickup-enabled-switch"
                                inputType={EditableFieldTypes.switch}
                                label={t("wmb.requestShuttlePickupEnabled")}
                                onProvide={(data) => data.request_shuttle_pickup_enabled == true}
                                onChange={(data, value) => {
                                  data.request_shuttle_pickup_enabled = value;
                                  setShuttlePickupEnabled(value);
                                }}
                              />
                              {shuttlePickupEnabled && (
                                <EditableField<IPreferences, IDropDownListItem>
                                  testId="settings-wmb-pickup-trigger-dropdown"
                                  inputType={EditableFieldTypes.enumDropDownField}
                                  label={t("wmb.pickupTrigger")}
                                  onProvide={(data) => {
                                    const items: IDropDownListItem[] = [
                                      { name: t("wmb.pickupTriggerOnCheckIn"), value: ShuttleBusPickupTrigger.OnCheckIn.toString() },
                                      { name: t("wmb.pickupTriggerOnRequest"), value: ShuttleBusPickupTrigger.OnRequest.toString() },
                                    ];
                                    return items.find(i => i.value === (data.pickup_trigger ?? ShuttleBusPickupTrigger.OnCheckIn).toString()) ?? items[0];
                                  }}
                                  onChange={(data, value) =>
                                    (data.pickup_trigger = parseInt(value.value ?? "0") as ShuttleBusPickupTrigger)
                                  }
                                  onRequestData={async () => [
                                    { name: t("wmb.pickupTriggerOnCheckIn"), value: ShuttleBusPickupTrigger.OnCheckIn.toString() },
                                    { name: t("wmb.pickupTriggerOnRequest"), value: ShuttleBusPickupTrigger.OnRequest.toString() },
                                  ]}
                                  onRequestText={(data) => {
                                    const items: IDropDownListItem[] = [
                                      { name: t("wmb.pickupTriggerOnCheckIn"), value: ShuttleBusPickupTrigger.OnCheckIn.toString() },
                                      { name: t("wmb.pickupTriggerOnRequest"), value: ShuttleBusPickupTrigger.OnRequest.toString() },
                                    ];
                                    return items.find(i => i.value === (data.pickup_trigger ?? ShuttleBusPickupTrigger.OnCheckIn).toString())?.name ?? items[0].name;
                                  }}
                                />
                              )}
                            </>
                          )}
                        </Grid>
                      </EditableGroup>
                    </NotchedCard>
                  </Grid>

                  <BusWalkAroundSettings onSave={savePrefs} />

                  {isDriverShiftTrackerReleased && <DriverShiftTrackerSettings onSave={savePrefs} />}

                  {isMeetAndGreetFeaturesSettingsReleased && <MeetAndGreetSettings onSave={savePrefs} />}

                  <ContactRecordSettings onSave={savePrefs} />

                  <BookingRecordSettings onSave={savePrefs} />

                  {bookingScanOptionsReleased && <BookingScanOptionSettings onSave={savePrefs} />}
                  {isScanningPageReleased && <ScanningPageSettings onSave={savePrefs} />}

                  <VehicleRecordSettings onSave={savePrefs} />

                  {isFlightRecordOptionsReleased && !isRenameOutboundReturnReleased && (
                    <OutboundFlightRecordSettings onSave={savePrefs} />
                  )}

                  {!isRenameOutboundReturnReleased && (
                    <ReturnFlightRecordSettings onSave={savePrefs} />
                  )}

                  <Grid item xs={12}>

                    <NotchedCard label={t("settings.vehicleLookup")} style={styles.card}>
                      <EditableGroup<IPreferences>
                        id="vehicleLookup"
                        testId="settings-vehicle-lookup-group"
                        editType={EditableGroupType.switchableEditing}
                        data={session.current.site.preferences}
                        onDataChanged={(data) => savePrefs({
                          booking_vehicle_dvla_enabled: data.booking_vehicle_dvla_enabled,
                          cleanAirZone: data.cleanAirZone,
                        })}
                      >
                        <Grid container>

                          <EditableField<IPreferences, boolean>
                            testId="settings-vehicle-dvla-enabled-switch"
                            inputType={EditableFieldTypes.switch}
                            label={t("reports.enabled")}
                            onProvide={(data) => data.booking_vehicle_dvla_enabled}
                            onChange={(data, value) => data.booking_vehicle_dvla_enabled = value}
                          />

                          <EditableField<IPreferences, IDropDownListItem>
                            testId="settings-vehicle-provider-field"
                            inputType={EditableFieldTypes.terminalField}
                            label={t("settings.provider")}
                            onProvide={() => {
                              return {
                                name: "DVLA",
                                value: "0"
                              };
                            }}
                            onChange={(data, item) => {
                              console.log(item);
                            }}
                            onRequestData={async () => {
                              return [{
                                name: "DVLA", value: "0"
                              }];
                            }}
                          />

                          {isDisplayCleanAirComplianceReleased && <EditableField<IPreferences, boolean>
                            testId="settings-clean-air-zone"
                            inputType={EditableFieldTypes.switch}
                            label={t("settings.cleanAirZone.enabled")}
                            onProvide={(data) => Boolean(data.cleanAirZone)}
                            onChange={(data, value) => data.cleanAirZone = value ? 1 : 0}
                          />}

                        </Grid>
                      </EditableGroup>
                    </NotchedCard>

                  </Grid>
                  {(isAdmin(session) && isBrandNameReleased) && <BrandSettings onSave={savePrefs} />}

                  {
                    isExternalIdReferencesReleased && (
                      <Grid item xs={12}>
                        <NotchedCard label={t("settings.trackingId")} style={styles.card}>
                          <EditableGroup<IPreferences>
                            id="trackingId"
                            testId="settings-tracking-id-group"
                            editType={EditableGroupType.switchableEditing}
                            data={session.current.site.preferences}
                            onDataChanged={(data) => {
                              if (isTrackingIdValidationReleased && data.tracking_id_regex) {
                                try {
                                  new RegExp(data.tracking_id_regex);
                                } catch (_error: unknown) {
                                  alert(t("settings.invalidRegex"));
                                  return Promise.resolve(false);
                                }
                              }

                              return savePrefs({
                                use_tracking_id: data.use_tracking_id,
                                tracking_id_regex: data.tracking_id_regex,
                              });
                            }}
                          >
                            <Grid container>
                              <EditableField<IPreferences, boolean>
                                testId="settings-use-tracking-id"
                                inputType={EditableFieldTypes.switch}
                                label={t("reports.enabled")}
                                onProvide={(data) => data.use_tracking_id || false}
                                onChange={(data, value) => data.use_tracking_id = value}
                              />

                              {session.current.site.preferences?.use_tracking_id && isTrackingIdValidationReleased && (
                                <EditableField<IPreferences, string>
                                  testId="settings-tracking-id-regex"
                                  inputType={EditableFieldTypes.text}
                                  label={t("settings.trackingIdRegex")}
                                  maxLength={255}
                                  onProvide={(data) => data.tracking_id_regex || ""}
                                  onChange={(data, value) => data.tracking_id_regex = value}
                                />
                              )}

                            </Grid>
                          </EditableGroup>
                        </NotchedCard>
                      </Grid>
                    )
                  }

                  <Grid item xs={12}>

                    <NotchedCard label={"Complaints"} style={styles.card}>
                      <EditableGroup<IPreferences>
                        id="complaintSettings"
                        testId="settings-complaints-group"
                        editType={EditableGroupType.switchableEditing}
                        data={session.current.site.preferences}
                        onDataChanged={(data) => savePrefs({
                          complaints_enabled: data.complaints_enabled,
                          complaints_allow_send_customer_enabled: data.complaints_allow_send_customer_enabled,
                        })}
                      >
                        <Grid container>
                          <EditableField<IPreferences, boolean>
                            testId="settings-complaints-enabled-switch"
                            inputType={EditableFieldTypes.switch}
                            label={t("reports.enabled")}
                            onProvide={(data) => data.complaints_enabled == true}
                            onChange={(data, value) => data.complaints_enabled = value}
                          />

                          <EditableField<IPreferences, boolean>
                            testId="settings-complaints-allow-send-customer-switch"
                            inputType={EditableFieldTypes.switch}
                            label={t("settings.allowSendToCustomer")}
                            onProvide={(data) => data.complaints_allow_send_customer_enabled == true}
                            onChange={(data, value) => data.complaints_allow_send_customer_enabled = value}
                          />


                        </Grid>
                      </EditableGroup>
                    </NotchedCard>

                  </Grid>

                  <Grid item xs={12}>

                    <NotchedCard label={t("booking.labels.inspections")} style={styles.card}>
                      <EditableGroup<IPreferences>
                        id="inspectionSettings"
                        testId="settings-inspections-group"
                        editType={EditableGroupType.switchableEditing}
                        data={session.current.site.preferences}
                        onDataChanged={(data) => savePrefs({
                          inspections_enabled: data.inspections_enabled,
                        })}
                      >
                        <Grid container>
                          <EditableField<IPreferences, boolean>
                            testId="settings-inspections-enabled-switch"
                            inputType={EditableFieldTypes.switch}
                            label={t("reports.enabled")}
                            onProvide={(data) => data.inspections_enabled == true}
                            onChange={(data, value) => data.inspections_enabled = value}
                          />
                        </Grid>
                      </EditableGroup>
                    </NotchedCard>

                  </Grid>

                  <Grid item xs={12}>
                    <NotchedCard label={isRenameOutboundReturnReleased ? t("settings.flightLookup") : t("settings.flights")} style={styles.card}>
                      <EditableGroup<IPreferences>
                        id="flightLookup"
                        testId="settings-flights-group"
                        editType={EditableGroupType.switchableEditing}
                        data={session.current.site.preferences}
                        onDataChanged={(data) => savePrefs({
                          booking_flightLookup_enabled: data.booking_flightLookup_enabled,
                          booking_flightValidation_enabled: data.booking_flightValidation_enabled,
                          ...(!isRenameOutboundReturnReleased && {
                            booking_outboundFlight_enabled: data.booking_outboundFlight_enabled,
                            booking_returnFlight_enabled: data.booking_returnFlight_enabled,
                          }),
                          flight_lookup_tolerance_hours: data.flight_lookup_tolerance_hours,
                          ...((isScheduledFlightValidatorReleased || isLiveFlightDataReleased) && {
                            flightLookupFieldOptions: data.flightLookupFieldOptions,
                          }),
                        })}
                      >
                        <Grid container>

                          <EditableField<IPreferences, boolean>
                            testId="settings-flight-lookup-switch"
                            inputType={EditableFieldTypes.switch}
                            label={isRenameOutboundReturnReleased ? t("settings.scheduledFlightData") : t("settings.lookup")}
                            onProvide={(data) => data.booking_flightLookup_enabled}
                            onChange={(data, value) => data.booking_flightLookup_enabled = value}
                          />

                          {(!isRenameOutboundReturnReleased || Boolean(session.current.site.preferences.booking_flightLookup_enabled)) && (
                            <EditableField<IPreferences, boolean>
                              testId="settings-flight-validation-switch"
                              inputType={EditableFieldTypes.switch}
                              label={t("settings.validation")}
                              onProvide={(data) => data.booking_flightValidation_enabled}
                              onChange={(data, value) => data.booking_flightValidation_enabled = value}
                            />
                          )}

                          {!isRenameOutboundReturnReleased && (
                            <EditableField<IPreferences, boolean>
                              testId="settings-show-outbound-flight-switch"
                              inputType={EditableFieldTypes.switch}
                              label={t("settings.showOutboundFlight")}
                              onProvide={(data) => data.booking_outboundFlight_enabled}
                              onChange={(data, value) => data.booking_outboundFlight_enabled = value}
                            />
                          )}

                          {!isRenameOutboundReturnReleased && (
                            <EditableField<IPreferences, boolean>
                              testId="settings-show-return-flight-switch"
                              inputType={EditableFieldTypes.switch}
                              label={"Show Return Flight"}
                              onProvide={(data) => data.booking_returnFlight_enabled}
                              onChange={(data, value) => data.booking_returnFlight_enabled = value}
                            />
                          )}

                          {(!isRenameOutboundReturnReleased || Boolean(session.current.site.preferences.booking_flightLookup_enabled)) && (
                            <EditableField<IPreferences, number>
                              testId="settings-flight-lookup-tolerance-field"
                              inputType={EditableFieldTypes.number}
                              label={t("settings.defaultFlightLookup")}
                              onProvide={(data) => data.flight_lookup_tolerance_hours ?? 3}
                              onChange={(data, value) => {
                                if (value > 0) {
                                  data.flight_lookup_tolerance_hours = value;
                                }
                              }}
                            />
                          )}

                          {isScheduledFlightValidatorReleased && (
                            <EditableField<IPreferences, boolean>
                              testId="settings-scheduled-flight-validator-switch"
                              inputType={EditableFieldTypes.switch}
                              label={t("settings.scheduledFlightValidator")}
                              onProvide={(data) => Boolean(data.flightLookupFieldOptions?.properties.scheduledFlightValidatorEnabled)}
                              onChange={(data, value) => ensureFlightLookupFieldOptions(data).properties.scheduledFlightValidatorEnabled = Boolean(value)}
                            />
                          )}

                          {isLiveFlightDataReleased && (
                            <EditableField<IPreferences, boolean>
                              testId="settings-live-flight-data-switch"
                              inputType={EditableFieldTypes.switch}
                              label={t("settings.liveFlightData")}
                              onProvide={(data) => Boolean(data.flightLookupFieldOptions?.properties.liveFlightDataEnabled)}
                              onChange={(data, value) => ensureFlightLookupFieldOptions(data).properties.liveFlightDataEnabled = Boolean(value)}
                            />
                          )}

                          {(isScheduledFlightValidatorReleased || isLiveFlightDataReleased) && (
                            <EditableField<IPreferences, number>
                              testId="settings-flight-validation-window-field"
                              inputType={EditableFieldTypes.number}
                              label={t("settings.flightValidationWindow")}
                              onProvide={(data) => data.flightLookupFieldOptions?.properties.validationWindowHours ?? 11}
                              onChange={(data, value) => {
                                if (value > 0) {
                                  ensureFlightLookupFieldOptions(data).properties.validationWindowHours = value;
                                }
                              }}
                            />
                          )}
                        </Grid>
                      </EditableGroup>
                    </NotchedCard>
                  </Grid>

                  <Grid item xs={12}>

                    <NotchedCard label={t("settings.data")} style={styles.card}>
                      <EditableGroup<IPreferences>
                        id="dataSettings"
                        testId="settings-data-group"
                        editType={EditableGroupType.switchableEditing}
                        data={session.current.site.preferences}
                        onDataChanged={(data) => savePrefs({
                          paging_limit: data.paging_limit,
                          date_format: data.date_format,
                          date_format_without_time: data.date_format_without_time,
                          time_zone: data.time_zone,
                          default_country_code: data.default_country_code,
                          default_report_category: data.default_report_category,
                        })}
                      >
                        <Grid container>
                          <EditableField<IPreferences, number>
                            testId="settings-data-paging-limit-field"
                            inputType={EditableFieldTypes.number}
                            label={t("settings.dataPagingLimit")}
                            onProvide={(data) => data.paging_limit}
                            onChange={(data, value) => data.paging_limit = value}
                          />
                          <EditableField<IPreferences, string>
                            testId="settings-date-time-format-field"
                            inputType={EditableFieldTypes.text}
                            label={t("settings.dateTimeFormat")}
                            onProvide={(data) => data.date_format}
                            onChange={(data, value) => data.date_format = value}
                          />
                          <EditableField<IPreferences, string>
                            testId="settings-date-format-field"
                            inputType={EditableFieldTypes.text}
                            label={t("settings.dateFormat")}
                            onProvide={(data) => data.date_format_without_time}
                            onChange={(data, value) => data.date_format_without_time = value}
                          />
                          <EditableField<IPreferences, string>
                            testId="settings-timezone-field"
                            inputType={EditableFieldTypes.text}
                            label={t("settings.timezone")}
                            onProvide={(data) => data.time_zone}
                            onChange={(data, value) => data.time_zone = value}
                          />
                          <EditableField<IPreferences, IDropDownListItem>
                            testId="settings-default-country-code-field"
                            inputType={EditableFieldTypes.enumDropDownField}
                            label={t("settings.defaultCountryCode")}
                            onProvide={(data) => ({ name: data.default_country_code, value: data.default_country_code })}
                            onChange={(data, value) => data.default_country_code = value.value ?? value.name}
                            onRequestData={async () => {
                              return getCountries().map((country) => ({
                                name: country,
                                value: country,
                              }));
                            }}
                            onRequestText={(data) => data.default_country_code}
                          />
                          <EditableField<IPreferences, string>
                            testId="settings-default-report-category-field"
                            inputType={EditableFieldTypes.text}
                            label={t("settings.defaultReportCat")}
                            onProvide={(data) => data.default_report_category}
                            onChange={(data, value) => data.default_report_category = value}
                          />
                        </Grid>
                      </EditableGroup>
                    </NotchedCard>
                  </Grid>

                  {(<Grid item xs={12}>
                    <NotchedCard label={t("menuItems.locations")} style={styles.card}>
                      <EditableGroup<IPreferences>
                        id="locations"
                        testId="settings-locations-group"
                        editType={EditableGroupType.switchableEditing}
                        data={session.current.site.preferences}
                        onDataChanged={(data) => savePrefs({
                          assetlocations_enabled: data.assetlocations_enabled,
                        })}
                      >
                        <Grid container>

                          <EditableField<IPreferences, boolean>
                            testId="settings-locations-enabled-switch"
                            inputType={EditableFieldTypes.switch}
                            label={t("reports.enabled")}
                            onProvide={(data) => data.assetlocations_enabled}
                            onChange={(data, value) => data.assetlocations_enabled = value}
                          />
                        </Grid>
                      </EditableGroup>
                    </NotchedCard>
                  </Grid>)}

                  <Grid item xs={12}>
                    <NotchedCard label={t("locations.keyMovements")} style={styles.card}>
                      <EditableGroup<IPreferences>
                        id="keyMovements"
                        testId="settings-key-movements-group"
                        editType={EditableGroupType.switchableEditing}
                        data={session.current.site.preferences}
                        onDataChanged={(data) => savePrefs({
                          keymovements_enabled: data.keymovements_enabled,
                        })}
                      >
                        <Grid container>
                          <EditableField<IPreferences, boolean>
                            testId="settings-key-movements-enabled-switch"
                            inputType={EditableFieldTypes.switch}
                            label={t("reports.enabled")}
                            onProvide={(data) => data.keymovements_enabled}
                            onChange={(data, value) => data.keymovements_enabled = value}
                          />
                        </Grid>
                      </EditableGroup>
                    </NotchedCard>
                  </Grid>

                  <Grid item xs={12}>
                    <NotchedCard
                      label={isSydneyDualParkAllocationReleased ? t("menuItems.laneAllocation") : t("menuItems.kmos")}
                      style={styles.card}
                    >
                      <EditableGroup<IPreferences>
                        id="kmos"
                        testId="settings-booking-settings-group"
                        editType={EditableGroupType.switchableEditing}
                        data={session.current.site.preferences}
                        onDataChanged={(data) => savePrefs({
                          lane_allocation_system: data.lane_allocation_system,
                          kmos_version: data.kmos_version,
                          kmos_prompt_reallocate_enabled: data.kmos_prompt_reallocate_enabled,
                          kmos_manual_allocation_enabled: data.kmos_manual_allocation_enabled,
                          dynamic_allocation_tier: data.dynamic_allocation_tier,
                          dynamic_allocation_paired_journeys: data.dynamic_allocation_paired_journeys,
                          dynamic_allocation_code: data.dynamic_allocation_code,
                        })}
                        onCancelEdit={() => setSelectedAllocationSystem(session.current.site?.preferences.lane_allocation_system ?? LaneAllocationSystem.None)}
                      >
                        <Grid container>
                          {
                            isSydneyDualParkAllocationReleased ? (
                              <>
                                <EditableField<IPreferences, IDropDownListItem>
                                  testId="settings-lane-allocation-system-field"
                                  inputType={EditableFieldTypes.enumDropDownField}
                                  label={t("laneAllocationSystem.labels.allocationSystem")}
                                  onRequestData={() => Promise.resolve(getLaneAllocationOptions())}
                                  onRequestText={(data) => t(laneAllocationMap[data.lane_allocation_system ?? LaneAllocationSystem.None])}
                                  onProvide={(data) => ({
                                    name: t(laneAllocationMap[data.lane_allocation_system ?? LaneAllocationSystem.None]),
                                    value: (data.lane_allocation_system ?? LaneAllocationSystem.None).toString()
                                  })}
                                  onChange={(data, value) => {
                                    const serviceValue = parseInt(value.value ?? "0");
                                    data.lane_allocation_system = serviceValue;
                                    setSelectedAllocationSystem(serviceValue);
                                  }}
                                />

                                {isMoveTimeSlotManagerReleased && (<Button
                                  data-testid="settings-kmos-config-time-slots-button"
                                  variant="contained"
                                  color="secondary"
                                  style={{ marginTop: "1.4em", marginBottom: "0.6em" }}
                                  onClick={() => {
                                    setShowTimeSlotEditor(true);
                                  }}
                                >
                                  {t("kmos.labels.configTimeSlotsButton")}
                                </Button>)}

                                {/* KMOS-specific settings */}
                                {selectedAllocationSystem === LaneAllocationSystem.KMOS && (
                                  <>
                                    {kmosV2Released && (
                                      <EditableField<IPreferences, IDropDownListItem>
                                        testId="settings-kmos-version-field"
                                        inputType={EditableFieldTypes.enumDropDownField}
                                        label={t("settings.version")}
                                        onRequestData={() => Promise.resolve([
                                          { name: "1.0", value: "1.0" },
                                          { name: "2.0", value: "2.0" },
                                        ])
                                        }
                                        onRequestText={(data) => data.kmos_version ?? t("settings.notSet")}
                                        onProvide={(data) => ({ name: data.kmos_version, value: data.kmos_version })}
                                        onChange={(data, value) => data.kmos_version = value.value || "1.0"}
                                      />
                                    )}

                                    {kmosManualAllocationReleased && (
                                      <Fragment>
                                        <EditableField<IPreferences, boolean>
                                          testId="settings-kmos-prompt-reallocate-switch"
                                          inputType={EditableFieldTypes.switch}
                                          label={t("settings.kmosPromptReAllocate")}
                                          onProvide={(data) => data.kmos_prompt_reallocate_enabled == true}
                                          onChange={(data, value) => data.kmos_prompt_reallocate_enabled = value}
                                        />
                                        <EditableField<IPreferences, boolean>
                                          testId="settings-kmos-manual-allocation-switch"
                                          inputType={EditableFieldTypes.switch}
                                          label={t("settings.kmosManualAllocation")}
                                          onProvide={(data) => data.kmos_manual_allocation_enabled == true}
                                          onChange={(data, value) => data.kmos_manual_allocation_enabled = value}
                                        />
                                      </Fragment>
                                    )}
                                  </>
                                )}

                                {/* DAS-specific settings */}
                                {(selectedAllocationSystem === LaneAllocationSystem.DAS && isDynamicAllocationPreferenceReleased) && (
                                  <>
                                    <EditableField<IPreferences, IDropDownListItem>
                                      testId="settings-dynamic-allocation-tier-field"
                                      inputType={EditableFieldTypes.enumDropDownField}
                                      label={t("settings.dynamicAllocationTier")}
                                      onRequestData={() => Promise.resolve([
                                        { name: t("settings.dynamicAllocationTierBasic"), value: DynamicAllocationTier.Basic.toString() },
                                        { name: t("settings.dynamicAllocationTierFull"), value: DynamicAllocationTier.Full.toString() },
                                      ])
                                      }
                                      onRequestText={(data) => DYNAMIC_ALLOCATION_TIER_LABELS.get(data.dynamic_allocation_tier)!}
                                      onProvide={(data) => ({
                                        name: DYNAMIC_ALLOCATION_TIER_LABELS.get(data.dynamic_allocation_tier)!,
                                        value: data.dynamic_allocation_tier.toString()
                                      })}
                                      onChange={(data, value) => data.dynamic_allocation_tier = parseInt(value.value ?? "0")}
                                    />
                                    <EditableField<IPreferences, string>
                                      testId="settings-dynamic-allocation-code-field"
                                      inputType={EditableFieldTypes.text}
                                      label={t("settings.dynamicAllocationCode")}
                                      onProvide={(data) =>
                                        data.dynamic_allocation_code ?? ""
                                      }
                                      onChange={(data, value) => {
                                        data.dynamic_allocation_code = value;
                                      }}
                                    />
                                    {isPairedJourneysReleased && (
                                      <EditableField<IPreferences, boolean>
                                        testId="settings-dynamic-allocation-paired-journeys-switch"
                                        inputType={EditableFieldTypes.switch}
                                        label={t("settings.dynamicAllocationPairedJourneys")}
                                        onProvide={(data) => data.dynamic_allocation_paired_journeys}
                                        onChange={(data, value) => data.dynamic_allocation_paired_journeys = value}
                                      />
                                    )}
                                  </>
                                )}
                              </>
                            ) : (
                              <>
                                <EditableField<IPreferences, boolean>
                                  testId="settings-kmos-enabled-switch"
                                  inputType={EditableFieldTypes.switch}
                                  label={t("reports.enabled")}
                                  onProvide={(data) => data.kmos_enabled}
                                  onChange={(data, value) => data.kmos_enabled = value}
                                />
                                {kmosV2Released && (
                                  <EditableField<IPreferences, IDropDownListItem>
                                    testId="settings-kmos-version-legacy-field"
                                    inputType={EditableFieldTypes.enumDropDownField}
                                    label={t("settings.version")}
                                    onRequestData={() => Promise.resolve([
                                      { name: "1.0", value: "1.0" },
                                      { name: "2.0", value: "2.0" },
                                    ])
                                    }
                                    onRequestText={(data) => data.kmos_version ?? t("settings.notSet")}
                                    onProvide={(data) => ({ name: data.kmos_version, value: data.kmos_version })}
                                    onChange={(data, value) => data.kmos_version = value.value || "1.0"}
                                  />
                                )}

                                {kmosManualAllocationReleased && (
                                  <Fragment>
                                    <EditableField<IPreferences, boolean>
                                      testId="settings-kmos-prompt-reallocate-legacy-switch"
                                      inputType={EditableFieldTypes.switch}
                                      label={t("settings.kmosPromptReAllocate")}
                                      onProvide={(data) => data.kmos_prompt_reallocate_enabled == true}
                                      onChange={(data, value) => data.kmos_prompt_reallocate_enabled = value}
                                    />
                                    <EditableField<IPreferences, boolean>
                                      testId="settings-kmos-manual-allocation-legacy-switch"
                                      inputType={EditableFieldTypes.switch}
                                      label={t("settings.kmosManualAllocation")}
                                      onProvide={(data) => data.kmos_manual_allocation_enabled == true}
                                      onChange={(data, value) => data.kmos_manual_allocation_enabled = value}
                                    />
                                  </Fragment>
                                )}

                                {isMoveTimeSlotManagerReleased && (<Button
                                  variant="contained"
                                  color="secondary"
                                  style={{ marginTop: "2em" }}
                                  onClick={() => {
                                    setShowTimeSlotEditor(true);
                                  }}
                                >
                                  {t("kmos.labels.configTimeSlotsButton")}
                                </Button>)}
                              </>
                            )
                          }
                        </Grid>
                      </EditableGroup>
                    </NotchedCard>
                  </Grid>

                  <KmosTimeSlotEditor
                    show={showTimeSlotEditor}
                    readonly={false}
                    onUpdate={updateTimeSlotEditor}
                    onClose={() => setShowTimeSlotEditor(false)}
                  ></KmosTimeSlotEditor>

                  <Grid item xs={12}>
                    <NotchedCard label={t("settings.carAndAway")} style={styles.card}>
                      <EditableGroup<IPreferences>
                        id="carAndAway"
                        testId="settings-car-and-away-group"
                        editType={EditableGroupType.switchableEditing}
                        data={session.current.site.preferences}
                        onDataChanged={(data) => savePrefs({
                          caa_enabled: data.caa_enabled,
                        })}
                      >
                        <Grid container>

                          <EditableField<IPreferences, boolean>
                            testId="settings-caa-enabled-switch"
                            inputType={EditableFieldTypes.switch}
                            label={t("reports.enabled")}
                            onProvide={(data) => data.caa_enabled}
                            onChange={(data, value) => data.caa_enabled = value}
                          />

                        </Grid>
                      </EditableGroup>
                    </NotchedCard>
                  </Grid>

                  <Grid item xs={12}>
                    <NotchedCard label={t("settings.auditing")} style={styles.card}>
                      <EditableGroup<IPreferences>
                        id="laneAudit"
                        testId="settings-auditing-group"
                        editType={EditableGroupType.switchableEditing}
                        data={session.current.site.preferences}
                        onDataChanged={(data) => savePrefs({
                          lane_audit_enabled: data.lane_audit_enabled,
                          enable_enforce_key_position_audit: data.enable_enforce_key_position_audit,
                        })}
                      >
                        <Grid container>

                          <EditableField<IPreferences, boolean>
                            testId="settings-lane-audits-switch"
                            inputType={EditableFieldTypes.switch}
                            label={t("settings.laneAudits")}
                            onProvide={(data) => data.lane_audit_enabled == true}
                            onChange={(data, value) => data.lane_audit_enabled = value}
                          />

                          {releaseKeyAudit6342 && (
                            <EditableField<IPreferences, boolean>
                              testId="settings-enforce-key-order-position-switch"
                              inputType={EditableFieldTypes.switch}
                              label={t("settings.enforceKeyOrderPosition")}
                              onProvide={(data) => data.enable_enforce_key_position_audit == true}
                              onChange={(data, value) => data.enable_enforce_key_position_audit = value}
                            />)}

                        </Grid>
                      </EditableGroup>
                    </NotchedCard>
                  </Grid>


                  <Grid item xs={12}>
                    <NotchedCard label={t("settings.reporting")} style={styles.card}>
                      <EditableGroup<IPreferences>
                        id="reporting"
                        testId="settings-reporting-group"
                        editType={EditableGroupType.switchableEditing}
                        data={session.current.site.preferences}
                        onDataChanged={(data) => savePrefs({
                          overview_enabled: data.overview_enabled,
                          reports_v1: data.reports_v1,
                          reports_v2: data.reports_v2,
                        })}
                      >
                        <Grid container>

                          <EditableField<IPreferences, boolean>
                            testId="settings-overview-enabled-switch"
                            inputType={EditableFieldTypes.switch}
                            label={t("menuItems.insights")}
                            onProvide={(data) => data.overview_enabled}
                            onChange={(data, value) => data.overview_enabled = value}
                          />
                          <EditableField<IPreferences, boolean>
                            testId="settings-reports-v1-switch"
                            inputType={EditableFieldTypes.switch}
                            label={t("settings.reportsV1")}
                            onProvide={(data) => data.reports_v1 == true}
                            onChange={(data, value) => data.reports_v1 = value}
                          />
                          <EditableField<IPreferences, boolean>
                            testId="settings-reports-v2-switch"
                            inputType={EditableFieldTypes.switch}
                            label={t("settings.reportsV2")}
                            onProvide={(data) => data.reports_v2 == true}
                            onChange={(data, value) => data.reports_v2 = value}
                          />
                        </Grid>
                      </EditableGroup>
                    </NotchedCard>
                  </Grid>



                  <Grid item xs={12}>
                    <NotchedCard label={t("settings.keyLocker")} style={styles.card}>
                      <EditableGroup<IPreferences>
                        id="labels"
                        testId="settings-key-locker-group"
                        editType={EditableGroupType.switchableEditing}
                        data={session.current.site.preferences}
                        onDataChanged={(data) => savePrefs({
                          keyLockerAllowKey_enabled: data.keyLockerAllowKey_enabled,
                          booking_locker_reprint_enabled: data.booking_locker_reprint_enabled,
                          allow_printing_labels_before_checkin: data.allow_printing_labels_before_checkin,
                        })}
                      >
                        <Grid container>
                          <EditableField<IPreferences, boolean>
                            testId="settings-customer-allow-key-retrieval-switch"
                            inputType={EditableFieldTypes.switch}
                            label={t("settings.customerAllowKeyRetrieval")}
                            onProvide={(data) => data.keyLockerAllowKey_enabled == true}
                            onChange={(data, value) => data.keyLockerAllowKey_enabled = value}
                          />
                          <EditableField<IPreferences, boolean>
                            testId="settings-allow-locker-qr-reprint-switch"
                            inputType={EditableFieldTypes.switch}
                            label={t("settings.allowLockerQRReprint")}
                            onProvide={(data) => data.booking_locker_reprint_enabled}
                            onChange={(data, value) => data.booking_locker_reprint_enabled = value}
                          />
                          <EditableField<IPreferences, boolean>
                            testId="settings-allow-label-printing-switch"
                            inputType={EditableFieldTypes.switch}
                            label={t("settings.allowLabelPrinting")}
                            onProvide={(data) => data.allow_printing_labels_before_checkin}
                            onChange={(data, value) => data.allow_printing_labels_before_checkin = value}
                          />

                        </Grid>

                      </EditableGroup>
                    </NotchedCard>
                  </Grid>

                  <Grid item xs={12}>
                    <NotchedCard label={t(deprecateArchiveReleased ? 'settings.autoCancelNonArrivals' : 'settings.autoArchiveNonArrivals')} style={styles.card}>
                      <EditableGroup<IPreferences>
                        id="nonArrivals"
                        testId="settings-auto-cancel-group"
                        editType={EditableGroupType.switchableEditing}
                        data={session.current.site.preferences}
                        onDataChanged={(data) => savePrefs({
                          auto_archive_enabled: data.auto_archive_enabled,
                          auto_archive_hours_after_bookingStartDate: data.auto_archive_hours_after_bookingStartDate,
                          auto_archive_cancellation_time_unit: data.auto_archive_cancellation_time_unit
                        })}
                      >
                        <Grid container>

                          <EditableField<IPreferences, boolean>
                            testId="settings-auto-archive-enabled-switch"
                            inputType={EditableFieldTypes.switch}
                            label={t("reports.enabled")}
                            onProvide={(data) => data.auto_archive_enabled}
                            onChange={(data, value) => data.auto_archive_enabled = value}
                          />

                          <EditableField<IPreferences, number>
                            testId="settings-auto-archive-hours-field"
                            inputType={EditableFieldTypes.number}
                            label={autoCancellationTimeUnitQuantityLabel()}
                            onProvide={(data) => data.auto_archive_hours_after_bookingStartDate}
                            onChange={(data, value) => data.auto_archive_hours_after_bookingStartDate = value}
                          />

                          {isAutoCancellationCalendarDaysReleased && <EditableField<IPreferences, IDropDownListItem>
                            inputType={EditableFieldTypes.enumDropDownField}
                            testId={"cancellation-time-unit-dropdown"}
                            label={t("settings.autoCancelTimeUnit")}
                            onProvide={(data) => ({
                              name: t(autoCancellationTimeUnitMap[data.auto_archive_cancellation_time_unit ?? AutoCancellationTimeUnit.Hours]),
                              value: (data.auto_archive_cancellation_time_unit ?? AutoCancellationTimeUnit.Hours).toString()
                            })}
                            onChange={(data, value) => {
                              {
                                const serviceValue = parseInt(value.value ?? "0");
                                data.auto_archive_cancellation_time_unit = serviceValue;
                              }
                            }}
                            onRequestData={() => Promise.resolve(getAutoCancellationTimeUnitOptions())}
                            onRequestText={(data) => {
                              if (data.auto_archive_cancellation_time_unit == null) return "";
                              return formatCamel(AutoCancellationTimeUnit[data.auto_archive_cancellation_time_unit])
                            }}
                          />}

                        </Grid>
                      </EditableGroup>
                    </NotchedCard>
                  </Grid>

                  <Grid item xs={12}>
                    <NotchedCard label={t("settings.autoMaskPII")} style={styles.card}>
                      <EditableGroup<IPreferences>
                        id="maskPII"
                        testId="settings-auto-mask-pii-group"
                        editType={EditableGroupType.switchableEditing}
                        data={session.current.site.preferences}
                        onDataChanged={(data) => savePrefs({
                          auto_maskPII_enabled: data.auto_maskPII_enabled,
                          auto_maskPII_days_after_CheckOutDate: data.auto_maskPII_days_after_CheckOutDate,
                        })}
                      >
                        <Grid container>

                          <EditableField<IPreferences, boolean>
                            testId="settings-auto-mask-pii-enabled-switch"
                            inputType={EditableFieldTypes.switch}
                            label={t("reports.enabled")}
                            onProvide={(data) => data.auto_maskPII_enabled}
                            onChange={(data, value) => data.auto_maskPII_enabled = value}
                          />

                          <EditableField<IPreferences, number>
                            testId="settings-auto-mask-pii-days-field"
                            inputType={EditableFieldTypes.number}
                            label={t("settings.daysAfterCheckout")}
                            onProvide={(data) => data.auto_maskPII_days_after_CheckOutDate}
                            onChange={(data, value) => data.auto_maskPII_days_after_CheckOutDate = value}
                          />

                        </Grid>
                      </EditableGroup>
                    </NotchedCard>
                  </Grid>

                  <Grid item xs={12}>
                    <NotchedCard label={t("settings.autoCheckout")} style={styles.card}>
                      <EditableGroup<IPreferences>
                        id="autoCheckOut"
                        testId="settings-auto-checkout-group"
                        editType={EditableGroupType.switchableEditing}
                        data={session.current.site.preferences}
                        onDataChanged={(data) => savePrefs({
                          auto_checkout_enabled: data.auto_checkout_enabled,
                          auto_checkout_hours_after_bookingEndDate: data.auto_checkout_hours_after_bookingEndDate,
                        })}
                      >
                        <Grid container>

                          <EditableField<IPreferences, boolean>
                            testId="settings-auto-checkout-enabled-switch"
                            inputType={EditableFieldTypes.switch}
                            label={t("reports.enabled")}
                            onProvide={(data) => data.auto_checkout_enabled}
                            onChange={(data, value) => data.auto_checkout_enabled = value}
                          />

                          <EditableField<IPreferences, number>
                            testId="settings-auto-checkout-hours-field"
                            inputType={EditableFieldTypes.number}
                            label={t("settings.hoursAfterBookingEnd")}
                            onProvide={(data) => data.auto_checkout_hours_after_bookingEndDate}
                            onChange={(data, value) => data.auto_checkout_hours_after_bookingEndDate = value}
                          />

                        </Grid>
                      </EditableGroup>
                    </NotchedCard>
                  </Grid>

                  <Grid item xs={12}>
                    <NotchedCard label={t("settings.pickBy")} style={styles.card}>
                      <EditableGroup<IPreferences>
                        id="pickBy"
                        testId="settings-pick-by-group"
                        editType={EditableGroupType.switchableEditing}
                        data={session.current.site.preferences}
                        onDataChanged={(data) => savePrefs({
                          picking_offset: data.picking_offset,
                        })}
                      >
                        <Grid container>
                          <EditableField<IPreferences, number>
                            testId="settings-picking-offset-field"
                            inputType={EditableFieldTypes.number}
                            label={t("settings.pickingOffset")}
                            onProvide={(data) => data.picking_offset}
                            onChange={(data, value) => data.picking_offset = Math.max(value, 0)}
                          />

                        </Grid>
                      </EditableGroup>
                    </NotchedCard>
                  </Grid>

                  <Grid item xs={12}>
                    <NotchedCard label={t("settings.deviceManager")} style={styles.card}>
                      <EditableGroup<IPreferences>
                        id="devices"
                        testId="settings-device-manager-group"
                        editType={EditableGroupType.switchableEditing}
                        data={session.current.site.preferences}
                        onDataChanged={(data) => savePrefs({
                          enable_device_manager: data.enable_device_manager,
                        })}
                      >
                        <Grid container>

                          <EditableField<IPreferences, boolean>
                            testId="settings-device-manager-enabled-switch"
                            inputType={EditableFieldTypes.switch}
                            label={t("reports.enabled")}
                            onProvide={(data) => data.enable_device_manager == true}
                            onChange={(data, value) => data.enable_device_manager = value}
                          />

                        </Grid>
                      </EditableGroup>
                    </NotchedCard>
                  </Grid>

                  <Grid item xs={12}>
                    <NotchedCard label={t("settings.earlyReturnFlag")} style={styles.card}>
                      <EditableGroup<IPreferences>
                        id="earlyReturn"
                        testId="settings-early-return-group"
                        editType={EditableGroupType.switchableEditing}
                        data={session.current.site.preferences}
                        onDataChanged={(data) => savePrefs({
                          early_return_enabled: data.early_return_enabled,
                        })}
                      >
                        <Grid container>

                          <EditableField<IPreferences, boolean>
                            testId="settings-early-return-enabled-switch"
                            inputType={EditableFieldTypes.switch}
                            label={t("reports.enabled")}
                            onProvide={(data) => data.early_return_enabled == true}
                            onChange={(data, value) => data.early_return_enabled = value}
                          />

                        </Grid>
                      </EditableGroup>
                    </NotchedCard>
                  </Grid>


                  <Grid item xs={12}>
                    <NotchedCard label={t("settings.mobilePrinting")} style={styles.card}>
                      <EditableGroup<IPreferences>
                        id="mobilePrinting"
                        testId="settings-mobile-printing-group"
                        editType={EditableGroupType.switchableEditing}
                        data={session.current.site.preferences}
                        onDataChanged={(data) => savePrefs({
                          enable_device_zebra_labels: data.enable_device_zebra_labels,
                        })}
                      >
                        <Grid container>

                          <EditableField<IPreferences, boolean>
                            testId="settings-mobile-printing-zebra-enabled-switch"
                            inputType={EditableFieldTypes.switch}
                            label={t("mobilePrintingZebra.enabled")}
                            onProvide={(data) => data.enable_device_zebra_labels == true}
                            onChange={(data, value) => data.enable_device_zebra_labels = value}
                          />

                        </Grid>
                      </EditableGroup>
                    </NotchedCard>
                  </Grid>
                  {isKioskLabelsReleased && (
                    <Grid item xs={12}>
                      <NotchedCard label={t("settings.kioskPrinting")} style={styles.card}>
                        <EditableGroup<IPreferences>
                          id="kioskPrinting"
                          testId="settings-kiosk-printing-group"
                          editType={EditableGroupType.switchableEditing}
                          data={session.current.site.preferences}
                          onDataChanged={(data) => savePrefs({
                            enable_kiosk_labels: data.enable_kiosk_labels,
                          })}
                        >
                          <Grid container>

                            <EditableField<IPreferences, boolean>
                              testId="settings-kiosk-printing-enabled-switch"
                              inputType={EditableFieldTypes.switch}
                              label={t("kioskLabels.enabled")}
                              onProvide={(data) => data.enable_kiosk_labels == true}
                              onChange={(data, value) => data.enable_kiosk_labels = value}
                            />

                          </Grid>
                        </EditableGroup>
                      </NotchedCard>
                    </Grid>
                  )}


                  {digitalCollectionReceiptReleased && (
                    <CommunicationSettings onSave={savePrefs} label={t("settings.collectionReceipt")} />
                  )}
                </Grid>
              </TabbedViewPage>

              <TabbedViewPage title={t("menuItems.settings")}>

                <Grid container>

                  <Grid item xs={12}>

                    <NotchedCard label={t("settings.browser")} style={styles.card}>
                      <EditableGroup<IPreferences>
                        id="browser"
                        testId="settings-browser-group"
                        editType={EditableGroupType.switchableEditing}
                        data={session.current.site.preferences}
                        onDataChanged={() => Promise.resolve(true)}
                      >
                        <Grid container>

                          <EditableField<IPreferences, boolean>
                            testId="settings-browser-animations-switch"
                            inputType={EditableFieldTypes.switch}
                            label={t("settings.animations")}
                            onProvide={() => {
                              return retrieveValue("animations", "1") == "1"
                            }}
                            onChange={(data, value) => {
                              storeValue("animations", value == true ? "1" : "0");
                            }}
                          />
                          <EditableField<IPreferences, boolean>
                            testId="settings-browser-remember-search-switch"
                            inputType={EditableFieldTypes.switch}
                            label={t("settings.rememberSearchRefresh")}
                            onProvide={() => {
                              return retrieveValue("storeSearch", "1") == "1"
                            }}
                            onChange={(data, value) => {
                              storeValue("storeSearch", value == true ? "1" : "0");
                              if (value != true) {
                                storeObject("search", null);
                              }
                            }}
                          />
                        </Grid>
                      </EditableGroup>
                    </NotchedCard>

                  </Grid>

                  {isAdmin(session) && (
                    <>
                      {(
                        <Grid item xs={12}>
                          <NotchedCard label={t("locations.locationManager")} style={styles.card}>
                            <EditableGroup<IPreferences>
                              id="locationsSettings"
                              testId="settings-location-manager-group"
                              editType={EditableGroupType.switchableEditing}
                              data={session.current.site.preferences}
                              onDataChanged={(data) => savePrefs({
                                display_mixed_lane_warning: data.display_mixed_lane_warning,
                                display_next_movement_date: data.display_next_movement_date,
                              })}
                            >
                              <Grid container>
                                <EditableField<IPreferences, boolean>
                                  testId="settings-mixed-lane-warning-switch"
                                  inputType={EditableFieldTypes.switch}
                                  label={t("settings.mixedLaneWarning")}
                                  onProvide={(data) => Boolean(data.display_mixed_lane_warning)}
                                  onChange={(data, value) => (data.display_mixed_lane_warning = value)}
                                />
                                <EditableField<IPreferences, boolean>
                                  testId="settings-display-next-movement-date-switch"
                                  inputType={EditableFieldTypes.switch}
                                  label={t("booking.labels.returnFlightDate")}
                                  onProvide={(data) => Boolean(data.display_next_movement_date)}
                                  onChange={(data, value) => (data.display_next_movement_date = value)}
                                />
                              </Grid>
                            </EditableGroup>
                          </NotchedCard>
                        </Grid>
                      )}

                      <Grid item xs={12}>
                        <NotchedCard label={t("settings.security")} style={styles.card}>
                          <EditableGroup<IPreferences>
                            id="securitySettings"
                            testId="settings-security-group"
                            editType={EditableGroupType.switchableEditing}
                            data={session.current.site.preferences}
                            onDataChanged={(data) => savePrefs({
                              two_factor_required: data.two_factor_required,
                              session_timeout_duration: data.session_timeout_duration,
                            })}
                          >
                            <Grid container>
                              <EditableField<IPreferences, boolean>
                                testId="settings-require-2fa-switch"
                                inputType={EditableFieldTypes.switch}
                                label={t("settings.require2FA")}
                                onProvide={(data) => {
                                  return data.two_factor_required == true;
                                }}
                                onChange={(data, value) => data.two_factor_required = value == true}
                              />
                              {isSessionInactivityTimeoutDurationReleased && (
                                <EditableField<IPreferences, IDropDownListItem>
                                  testId="settings-session-inactivity-timeout-field"
                                  inputType={EditableFieldTypes.enumDropDownField}
                                  label={t("settings.sessionInactivityTimeout")}
                                  onRequestData={() => Promise.resolve(
                                    Array.from({ length: 24 }, (_, i) => ({
                                      name: t("settings.sessionInactivityTimeoutUnit", { count: i + 1 }),
                                      value: (i + 1).toString()
                                    }))
                                  )}
                                  onRequestText={(data) => t("settings.sessionInactivityTimeoutUnit", { count: data.session_timeout_duration || 1 })}
                                  onProvide={(data) => ({
                                    name: t("settings.sessionInactivityTimeoutUnit", { count: data.session_timeout_duration || 1 }),
                                    value: (data.session_timeout_duration || 1).toString()
                                  })}
                                  onChange={(data, value) => {
                                    const sessionTimeoutDuration = parseInt(value.value ?? "1");
                                    data.session_timeout_duration = sessionTimeoutDuration;
                                  }}
                                />
                              )}
                            </Grid>
                          </EditableGroup>
                        </NotchedCard>
                      </Grid>

                      <Grid item xs={12}>
                        <NotchedCard label={t("menuItems.bookings")} style={styles.card}>
                          <EditableGroup<IPreferences>
                            id="bookingSettings"
                            testId="settings-settings-bookings-group"
                            editType={EditableGroupType.switchableEditing}
                            data={session.current.site.preferences}
                            onDataChanged={(data) => savePrefs({
                              booking_checkout_with_balance_enabled: data.booking_checkout_with_balance_enabled,
                              show_customer_last_seen_date: data.show_customer_last_seen_date,
                              undo_check_in: data.undo_check_in,
                              ...(isDefaultBookingDurationReleased ? {
                                defaultBookingDuration: normaliseDefaultBookingDuration(data),
                              } : {}),
                            })}
                          >
                            <Grid container>
                              <EditableField<IPreferences, boolean>
                                testId="settings-allow-checkout-outstanding-balance-switch"
                                inputType={EditableFieldTypes.switch}
                                label={t("settings.allowCheckoutOutstandingBalance")}
                                onProvide={(data) => data.booking_checkout_with_balance_enabled}
                                onChange={(data, value) => data.booking_checkout_with_balance_enabled = value}
                              />
                              {isUndoCheckInReleased && (
                                <EditableField<IPreferences, boolean>
                                  testId="settings-undo-check-in-switch"
                                  inputType={EditableFieldTypes.switch}
                                  label={t("settings.undoCheckIn")}
                                  onProvide={(data) => data.undo_check_in || false}
                                  onChange={(data, value) => data.undo_check_in = value}
                                />
                              )}
                              {isBookingLastSeenDateReleased && (
                                <EditableField<IPreferences, boolean>
                                  testId="settings-undo-check-in-switch"
                                  inputType={EditableFieldTypes.switch}
                                  label={t("settings.showCustomerLastSeenDate")}
                                  onProvide={(data) => data.show_customer_last_seen_date || false}
                                  onChange={(data, value) => data.show_customer_last_seen_date = value}
                                />
                              )}
                              {isDefaultBookingDurationReleased && (
                                <EditableGroupContext.Consumer>
                                  {({ data }) => {
                                    const preferences = data as IPreferences | undefined;
                                    const isDefaultBookingDurationEnabled = preferences?.defaultBookingDuration?.enabled === true;

                                    return (
                                      <Grid item xs={12}>
                                        <Grid container>
                                          <EditableField<IPreferences, boolean>
                                            testId="settings-default-booking-duration-enabled-switch"
                                            inputType={EditableFieldTypes.switch}
                                            label={t("settings.defaultBookingDuration")}
                                            onProvide={(data) => data.defaultBookingDuration?.enabled === true}
                                            onChange={(data, enabled) => {
                                              data.defaultBookingDuration = {
                                                enabled,
                                                quantity: data.defaultBookingDuration?.quantity && data.defaultBookingDuration.quantity > 0
                                                  ? data.defaultBookingDuration.quantity
                                                  : 1,
                                                type: BookingDurationTimeUnit.Days,
                                              };
                                            }}
                                          />
                                          {isDefaultBookingDurationEnabled && (
                                            <EditableField<IPreferences, number>
                                              testId="settings-default-booking-duration-quantity-field"
                                              inputType={EditableFieldTypes.number}
                                              label={t("settings.defaultBookingDurationQuantityDays")}
                                              onProvide={(data) => data.defaultBookingDuration?.quantity ?? 1}
                                              onChange={(data, value) => {
                                                data.defaultBookingDuration = {
                                                  enabled: true,
                                                  quantity: Number.isInteger(value) && value > 0 ? value : 1,
                                                  type: BookingDurationTimeUnit.Days,
                                                };
                                              }}
                                            />
                                          )}
                                        </Grid>
                                      </Grid>
                                    );
                                  }}
                                </EditableGroupContext.Consumer>
                              )}
                            </Grid>
                          </EditableGroup>
                        </NotchedCard>
                      </Grid>

                      {(manageVehicleIssueTypesReleased) && (
                        <Grid item xs={12}>
                          <NotchedCard label={t("settings.laneAudits")} style={styles.card}>
                            <EditableGroup<IPreferences>
                              id="laneAuditSettings"
                              testId="settings-lane-audits-group"
                              editType={EditableGroupType.switchableEditing}
                              data={session.current.site.preferences}
                              onDataChanged={(data) => savePrefs({
                                active_vehicle_issue_types: data.active_vehicle_issue_types,
                              })}
                            >
                              {vehicleIssueTypes?.map((category) => (
                                <Fragment key={category.id}>
                                  <p className={cssClasses.subHeading}><strong>{category.title}</strong></p>
                                  <Grid container>
                                    {category.issueTypes.map((issueType) => (
                                      <EditableField<IPreferences, boolean>
                                        key={issueType.id}
                                        testId={`settings-vehicle-issue-type-${issueType.id}-switch`}
                                        inputType={EditableFieldTypes.switch}
                                        label={issueType.title}
                                        onProvide={(data) => Boolean(data.active_vehicle_issue_types?.includes(issueType.id))}
                                        onChange={(data, value) => {
                                          const activeTypes = new Set(data.active_vehicle_issue_types || []);
                                          if (value) {
                                            activeTypes.add(issueType.id);
                                          } else {
                                            activeTypes.delete(issueType.id);
                                          }
                                          data.active_vehicle_issue_types = Array.from(activeTypes);
                                        }}
                                      />
                                    ))}
                                  </Grid>
                                </Fragment>
                              ))}
                            </EditableGroup>
                          </NotchedCard>
                        </Grid>
                      )}

                      {isKeyAuditDueCriteriaReleased && (
                        isKeyAuditDueCriteriaTextReleased
                          ? <KeyAuditSettingsV2 onSave={savePrefs} />
                          : <KeyAuditSettings onSave={savePrefs} />
                      )}

                      {/* keeping the old key master settings on the settings page if key master feature is not released */}
                      {session.current.site.preferences.kmos_enabled && !isKeyMasterRoleReleased && (
                        <Grid item xs={12}>
                          <NotchedCard label={t("menuItems.kmos")} style={styles.card}>
                            <EditableGroup<IPreferences>
                              id="kmos"
                              testId="settings-kmos-group"
                              editType={EditableGroupType.switchableEditing}
                              data={session.current.site.preferences}
                              onDataChanged={(data) => savePrefs({
                                kmos_days_to_display: data.kmos_days_to_display,
                                kmos_duein_hours_ahead: data.kmos_duein_hours_ahead,
                              })}
                            >
                              <Grid container>
                                <EditableField<IPreferences, number>
                                  testId="settings-kmos-display-days-field"
                                  inputType={EditableFieldTypes.number}
                                  label={t("settings.displayDays")}
                                  onProvide={(data) => data.kmos_days_to_display}
                                  onChange={(data, value) => data.kmos_days_to_display = value}
                                />

                                <EditableField<IPreferences, number>
                                  testId="settings-kmos-due-in-hours-ahead-field"
                                  inputType={EditableFieldTypes.number}
                                  label={t("settings.dueInHoursAhead")}
                                  onProvide={(data) => data.kmos_duein_hours_ahead || 1}
                                  onChange={(data, value) => data.kmos_duein_hours_ahead = Math.max(0, value)}
                                />
                              </Grid>
                            </EditableGroup>
                          </NotchedCard>
                        </Grid>
                      )}

                      {distanceSettingReleased && <Grid item xs={12}>
                        <NotchedCard label={t("settings.movements")} style={styles.card}>
                          <EditableGroup<IPreferences>
                            id="movementSettings"
                            testId="settings-movements-group"
                            editType={EditableGroupType.switchableEditing}
                            data={session.current.site.preferences}
                            onDataChanged={(data) => savePrefs({
                              distance_unit: data.distance_unit,
                            })}
                          >
                            <Grid container>
                              <EditableField<IPreferences, IDropDownListItem>
                                testId="settings-distance-unit-field"
                                inputType={EditableFieldTypes.enumDropDownField}
                                label={t("settings.distanceUnit")}
                                onProvide={(data) => {
                                  return {
                                    name: "",
                                    value:
                                      data.distance_unit == null ? "0" : data.distance_unit.toString(),
                                  };
                                }}
                                onRequestText={(data) => {
                                  if (data.distance_unit == null) return "";
                                  return formatCamel(DistanceUnit[data.distance_unit]);
                                }}
                                onChange={(data, value) => {
                                  data.distance_unit = parseInt(value.value ?? "");
                                }}
                                onRequestData={async () => {
                                  const items: IDropDownListItem[] = [];
                                  for (const unit in DistanceUnit) {
                                    if (isNaN(parseInt(unit))) {
                                      items.push({
                                        name: formatCamel(unit.toString()),
                                        value: DistanceUnit[unit],
                                      });
                                    }
                                  }
                                  return items;
                                }}
                              />
                            </Grid>
                          </EditableGroup>
                        </NotchedCard>
                      </Grid>}
                    </>
                  )}

                  {isSydneyDualParkAllocationReleased && selectedAllocationSystem !== LaneAllocationSystem.None && canUpdateKmosRules && (
                    <Grid item xs={12}>
                      <NotchedCard label={t("menuItems.laneAllocation")} style={styles.card}>
                        <EditableGroup<IPreferences>
                          id="laneAllocationSettings"
                          testId="settings-lane-allocation-group"
                          editType={EditableGroupType.none}
                          data={session.current.site.preferences}
                          onDataChanged={(data) => savePrefs(data)}
                        >
                          <Grid container>
                            <Grid item xs={12} sm={6}>
                              <Button
                                startIcon={<FiGitBranch />}
                                data-testid="lane-allocation-rules-button"
                                style={{ margin: "10px 0" }}
                                variant="contained"
                                color="secondary"
                                onClick={() => {
                                  setShowLaneAllocationRulesView(true);
                                }}
                              >
                                {t("kmos.labels.rules")}
                              </Button>
                              <LaneAllocationView
                                show={showLaneAllocationRulesView}
                                onClose={() => {
                                  setShowLaneAllocationRulesView(false);
                                }}
                              />
                            </Grid>
                          </Grid>
                        </EditableGroup>
                        <Grid item xs={12} sm={6}>
                          {isTenantCopyReleased && session.current.site.preferences.lane_allocation_system === LaneAllocationSystem.DAS && (
                            <GenerateRecommendationsButton />
                          )}
                        </Grid>
                      </NotchedCard>
                    </Grid>
                  )}
                  {isRenameOutboundReturnReleased && isFlightRecordOptionsReleased && (
                    <OutboundFlightRecordSettings onSave={savePrefs} />
                  )}

                  {isRenameOutboundReturnReleased && (
                    <ReturnFlightRecordSettings onSave={savePrefs} />
                  )}

                  {isBrandNameReleased && isAdmin(session) && (
                    <BrandManagementSettings
                      onSave={savePrefs}
                    />
                  )}
                  {isDriverShiftTrackerReleased && isAdmin(session) && (
                    <DriverAvailabilitySettings
                      onSave={savePrefs}
                    />
                  )}

                  {showShuttlePickupCard && (
                    <ShuttlePickupSettings
                      onSave={savePrefs}
                      showFields={showShuttlePickupFields}
                      showPickupLocationManagement={showPickupLocationManagement}
                    />
                  )}
                  {isAddNoticePeriodToSettingsReleased && (
                    <NoticePeriodSettings onSave={savePrefs} />
                  )}



                </Grid>
              </TabbedViewPage>

              <TabbedViewPage visible={canViewWebhooks} title={t("settings.webhooks")}>
                <Grid container>
                  <Grid item xs={12}>
                    <WebhooksManager />
                  </Grid>
                </Grid>
              </TabbedViewPage>

              {isTenantCopyReleased && (
                <TabbedViewPage visible={isAdmin(session)} title={t("settings.tasks")}>
                  <Grid container>
                    <Grid item xs={12}>
                      <TasksSection />
                    </Grid>
                  </Grid>
                </TabbedViewPage>
              )}

              <TabbedViewPage visible={isAdmin(session)} title={t("settings.company")}>
                <Grid container>


                  <Grid item xs={12}>
                    <NotchedCard label={t("settings.companyDetails")} style={styles.card}>
                      <EditableGroup<IPreferences>
                        id="company"
                        testId="settings-company-details-group"
                        editType={EditableGroupType.switchableEditing}
                        data={session.current.site.preferences}
                        onDataChanged={(data) => savePrefs({
                          company_name: data.company_name,
                          company_email: data.company_email,
                          company_telephone: data.company_telephone,
                          company_website: data.company_website,
                        })}
                      >
                        <Grid container>
                          <EditableField<IPreferences, string>
                            testId="settings-company-name-field"
                            inputType={EditableFieldTypes.text}
                            label={t("settings.companyName")}
                            onProvide={(data) => data.company_name}
                            onChange={(data, value) => data.company_name = value}
                          />
                          <EditableField<IPreferences, string>
                            testId="settings-company-email-field"
                            inputType={EditableFieldTypes.text}
                            label={t("input.labels.email")}
                            onProvide={(data) => data.company_email}
                            onChange={(data, value) => data.company_email = value}
                          />
                          <EditableField<IPreferences, string>
                            testId="settings-company-telephone-field"
                            inputType={EditableFieldTypes.text}
                            label={t("input.labels.telephone")}
                            onProvide={(data) => data.company_telephone}
                            onChange={(data, value) => data.company_telephone = value}
                          />
                          <EditableField<IPreferences, string>
                            testId="settings-company-website-field"
                            inputType={EditableFieldTypes.text}
                            label={t("input.labels.website")}
                            onProvide={(data) => data.company_website}
                            onChange={(data, value) => data.company_website = value}
                          />
                        </Grid>
                      </EditableGroup>
                    </NotchedCard>
                  </Grid>
                  <Grid item xs={6}>
                    <NotchedCard label={t("input.labels.address")} style={styles.card}>
                      <EditableGroup<IPreferences>
                        id="address"
                        testId="settings-address-group"
                        editType={EditableGroupType.switchableEditing}
                        data={session.current.site.preferences}
                        onDataChanged={(data) => savePrefs({
                          company_address_line1: data.company_address_line1,
                          company_address_line2: data.company_address_line2,
                          company_address_line3: data.company_address_line3,
                          company_address_postcode: data.company_address_postcode,
                          company_what3words: data.company_what3words,
                        })}
                      >
                        {isWhat3wordsToSettingsReleased ? (
                          <Grid container>
                            <EditableField<IPreferences, string>
                              testId="settings-company-address-line1-field"
                              inputType={EditableFieldTypes.text}
                              label={t("input.labels.addressLine1")}
                              onProvide={(data) => data.company_address_line1}
                              onChange={(data, value) => data.company_address_line1 = value}
                            />
                            <EditableField<IPreferences, string>
                              testId="settings-company-address-line2-field"
                              inputType={EditableFieldTypes.text}
                              label={t("input.labels.addressLine2")}
                              onProvide={(data) => data.company_address_line2}
                              onChange={(data, value) => data.company_address_line2 = value}
                            />
                            <EditableField<IPreferences, string>
                              testId="settings-company-address-line3-field"
                              inputType={EditableFieldTypes.text}
                              label={t("input.labels.addressLine3")}
                              onProvide={(data) => data.company_address_line3}
                              onChange={(data, value) => data.company_address_line3 = value}
                            />
                            <EditableField<IPreferences, string>
                              testId="settings-company-address-postcode-field"
                              inputType={EditableFieldTypes.text}
                              label={t("input.labels.postcode")}
                              onProvide={(data) => data.company_address_postcode}
                              onChange={(data, value) => data.company_address_postcode = value}
                            />{isWhat3wordsToSettingsReleased && (<EditableField<IPreferences, string>
                              testId="settings-company-what3words-field"
                              inputType={EditableFieldTypes.text}
                              label={t("input.labels.what3words")}
                              placeholder={t("input.placeholders.what3words")}
                              onProvide={(data) => data.company_what3words ?? ""}
                              onChange={(data, value) => {
                                if (!value) {
                                  data.company_what3words = "";
                                  return;
                                }
                                let normalized = value.replace(/^\/{4,}/, "///");
                                if (!normalized.startsWith("/")) {
                                  normalized = `///${normalized}`;
                                }
                                data.company_what3words = normalized;
                              }}
                            />)}
                          </Grid>
                        ) : (<Grid container>
                          <EditableField<IPreferences, string>
                            testId="settings-company-address-line1-field"
                            inputType={EditableFieldTypes.text}
                            label={t("input.labels.addressLine1")}
                            onProvide={(data) => data.company_address_line1}
                            onChange={(data, value) => data.company_address_line1 = value}
                          />
                          <EditableField<IPreferences, string>
                            inputType={EditableFieldTypes.empty}
                            label={""}
                          />
                          <EditableField<IPreferences, string>
                            testId="settings-company-address-line2-field"
                            inputType={EditableFieldTypes.text}
                            label={t("input.labels.addressLine2")}
                            onProvide={(data) => data.company_address_line2}
                            onChange={(data, value) => data.company_address_line2 = value}
                          />
                          <EditableField<IPreferences, string>
                            inputType={EditableFieldTypes.empty}
                            label={""}
                          />
                          <EditableField<IPreferences, string>
                            testId="settings-company-address-line3-field"
                            inputType={EditableFieldTypes.text}
                            label={t("input.labels.addressLine3")}
                            onProvide={(data) => data.company_address_line3}
                            onChange={(data, value) => data.company_address_line3 = value}
                          />
                          <EditableField<IPreferences, string>
                            inputType={EditableFieldTypes.empty}
                            label={""}
                          />
                          <EditableField<IPreferences, string>
                            testId="settings-company-address-postcode-field"
                            inputType={EditableFieldTypes.text}
                            label={t("input.labels.postcode")}
                            onProvide={(data) => data.company_address_postcode}
                            onChange={(data, value) => data.company_address_postcode = value}
                          />
                        </Grid>)}
                      </EditableGroup>
                    </NotchedCard>
                  </Grid>

                  <Grid item xs={6}>
                    <NotchedCard label={t("settings.additional")} style={styles.card}>
                      <EditableGroup<IPreferences>
                        id="additional"
                        testId="settings-additional-group"
                        editType={EditableGroupType.switchableEditing}
                        data={session.current.site.preferences}
                        onDataChanged={(data) => savePrefs({
                          company_registration: data.company_registration,
                          company_vatno: data.company_vatno,
                          company_logo_base64: data.company_logo_base64,
                        })}
                      >
                        <Grid container>
                          <EditableField<IPreferences, string>
                            testId="settings-company-registration-field"
                            inputType={EditableFieldTypes.text}
                            label={t("settings.companyReg")}
                            onProvide={(data) => data.company_registration}
                            onChange={(data, value) => data.company_registration = value}
                          />
                          <EditableField<IPreferences, string>
                            testId="settings-company-tax-number-field"
                            inputType={EditableFieldTypes.text}
                            label={t("settings.taxNumber")}
                            onProvide={(data) => data.company_vatno}
                            onChange={(data, value) => data.company_vatno = value}
                          />
                          <EditableField<IPreferences, string>
                            testId="settings-company-logo-field"
                            inputType={EditableFieldTypes.image}
                            label={t("settings.companyLogo")}
                            onProvide={(data) => data.company_logo_base64}
                            onChange={(data, value) => data.company_logo_base64 = value}

                          />
                        </Grid>
                      </EditableGroup>
                    </NotchedCard>
                  </Grid>

                  <Grid item xs={12}>
                    <NotchedCard label={t("settings.distributionLists")} style={styles.card}>
                      <EditableGroup<IPreferences>
                        id="distributionSettings"
                        testId="settings-distribution-lists-group"
                        editType={EditableGroupType.alwaysEditing}
                        data={session.current.site.preferences}
                        dataFields={["complaints_distribution_category", "kiosk_assistance_category", "walkaround_distribution_category"]}
                        onDataChanged={(data) => savePrefs({
                          complaints_distribution_category: data.complaints_distribution_category,
                          kiosk_assistance_category: data.kiosk_assistance_category,
                          walkaround_distribution_category: data.walkaround_distribution_category,
                        })}
                      >
                        <Grid container>
                          <EditableField<IPreferences, Category | undefined>
                            testId="settings-complaints-email-recipients-field"
                            inputType={EditableFieldTypes.distributionList}
                            label={t("settings.complaintsEmailRecipients")}
                            onProvide={(data) => data.complaints_distribution_category}
                            onChange={(data, value) => data.complaints_distribution_category = value}
                          />
                          <EditableField<IPreferences, Category | undefined>
                            testId="settings-kiosk-assistance-sms-field"
                            inputType={EditableFieldTypes.distributionList}
                            label={t("settings.kioskAssistanceSMS")}
                            onProvide={(data) => data.kiosk_assistance_category}
                            onChange={(data, value) => data.kiosk_assistance_category = value}
                          />
                          {session.current.site.preferences.bus_walkaround_enabled == true && (
                            <EditableField<IPreferences, Category | undefined>
                              testId="settings-bus-walkaround-reports-field"
                              inputType={EditableFieldTypes.distributionList}
                              label={t("settings.busWalkaroundReports")}
                              onProvide={(data) => data.walkaround_distribution_category}
                              onChange={(data, value) => data.walkaround_distribution_category = value}
                            />
                          )}

                          <div style={{ marginTop: 30, marginLeft: 10 }}>
                            <DistributionListField manageOnly={true} onChange={() => null} label={t("settings.distributionLists")} />
                          </div>
                        </Grid>
                      </EditableGroup>
                    </NotchedCard>
                  </Grid>

                  <Grid item xs={12}>
                    <NotchedCard label={t("settings.opsAlert")} style={styles.card}>

                      <EditableGroup<IPreferences>
                        id="opsAlertSettings"
                        testId="settings-ops-alert-group"
                        editType={EditableGroupType.switchableEditing}
                        data={session.current.site.preferences}
                        onDataChanged={(data) => savePrefs({
                          ops_alerts_enabled: data.ops_alerts_enabled,
                          ops_alerts_distribution_category: data.ops_alerts_distribution_category,
                        })}
                      >
                        <Grid container>

                          <EditableField<IPreferences, boolean>
                            testId="settings-ops-alerts-enabled-switch"
                            inputType={EditableFieldTypes.switch}
                            label={t("reports.enabled")}
                            onProvide={(data) => data.ops_alerts_enabled}
                            onChange={(data, value) => data.ops_alerts_enabled = value}
                          />

                          <EditableField<IPreferences, Category | undefined>
                            testId="settings-ops-alerts-recipients-field"
                            inputType={EditableFieldTypes.distributionList}
                            label={t("settings.recipients")}
                            onProvide={(data) => data.ops_alerts_distribution_category}
                            onChange={(data, value) => data.ops_alerts_distribution_category = value}
                          />


                        </Grid>
                      </EditableGroup>

                    </NotchedCard>
                  </Grid>
                  {
                    shouldRenderPaymentMethodSetting && (
                      <PaymentMethodSettings
                        preferences={session.current.site.preferences}
                        onSave={savePrefs}
                      />
                    )}
                  <Grid item xs={12}>

                    <NotchedCard label={t("settings.rates")} style={styles.card}>
                      <EditableGroup<IPreferences>
                        id="rateSettings"
                        testId="settings-rates-group"
                        editType={EditableGroupType.switchableEditing}
                        data={session.current.site.preferences}
                        onDataChanged={(data) => savePrefs({
                          overstay_rate: data.overstay_rate,
                          walkin_rate: data.walkin_rate,
                          additional_time_rate: data.additional_time_rate,
                          cost_of_sale: data.cost_of_sale,
                          currency: data.currency,
                          vat_rate: data.vat_rate,
                          vat_name: data.vat_name,
                        })}
                      >
                        <Grid container>

                          <EditableField<IPreferences, number>
                            testId="settings-overstays-rate-field"
                            inputType={EditableFieldTypes.money}
                            label={t("settings.overstaysRate")}
                            onProvide={(data) => data.overstay_rate}
                            onChange={(data, value) => data.overstay_rate = value}
                          />
                          <EditableField<IPreferences, number>
                            testId="settings-walkin-rate-field"
                            inputType={EditableFieldTypes.money}
                            label={t("settings.walkinRate")}
                            onProvide={(data) => data.walkin_rate}
                            onChange={(data, value) => data.walkin_rate = value}
                          />
                          <EditableField<IPreferences, number>
                            testId="settings-add-days-rate-field"
                            inputType={EditableFieldTypes.money}
                            label={t("settings.addDaysRate")}
                            onProvide={(data) => data.additional_time_rate}
                            onChange={(data, value) => data.additional_time_rate = value}
                          />
                          <EditableField<IPreferences, number>
                            testId="settings-cost-of-sale-field"
                            inputType={EditableFieldTypes.money}
                            label={t("settings.costOfSale")}
                            onProvide={(data) => data.cost_of_sale}
                            onChange={(data, value) => data.cost_of_sale = value}
                          />
                          <EditableField<IPreferences, string>
                            testId="settings-currency-field"
                            inputType={EditableFieldTypes.textSingle}
                            label={t("settings.currency")}
                            onProvide={(data) => data.currency}
                            onChange={(data, value) => data.currency = value}
                          />
                          {!additionalTaxRatesReleased &&
                            <EditableField<IPreferences, number>
                              testId="settings-default-tax-rate-field"
                              inputType={EditableFieldTypes.percentage}
                              label={t("settings.defaultTaxRate")}
                              onProvide={(data) => data.vat_rate}
                              onChange={(data, value) => data.vat_rate = value}
                            />
                          }
                          {isSysAdmin(session) && !additionalTaxRatesReleased && (
                            <EditableField<IPreferences, string>
                              testId="settings-local-tax-name-field"
                              inputType={EditableFieldTypes.text}
                              label={t("settings.localTax")}
                              onProvide={(data) => data.vat_name}
                              onChange={(data, value) => data.vat_name = value}
                            />
                          )}
                        </Grid>
                      </EditableGroup>
                    </NotchedCard>
                  </Grid>
                  {additionalTaxRatesReleased && <Grid item xs={12}>
                    <NotchedCard label={t("settings.taxes")} style={styles.card}>
                      <EditableGroup<IPreferences>
                        id="taxSettings"
                        testId="settings-taxes-group"
                        editType={EditableGroupType.switchableEditing}
                        data={session.current.site.preferences}
                        onDataChanged={(data) => savePrefs({
                          vat_name: data.vat_name,
                          vat_rate: data.vat_rate,
                          vat_name_2: data.vat_name_2,
                          vat_rate_2: data.vat_rate_2,
                          company_taxName: data.company_taxName,
                        })}
                        onCancelEdit={() => { setTaxRates(initialTaxRates) }}
                      >
                        <Grid container>
                          {taxRates.map((taxRate, index) => {
                            return <Fragment key={index}>
                              {isSysAdmin(session) && (
                                <EditableField<IPreferences, string>
                                  testId={`settings-tax-name-${index + 1}-field`}
                                  inputType={EditableFieldTypes.text}
                                  label={t("settings.taxNameIndex", { count: index + 1 })}
                                  onProvide={() => taxRate.name ? taxRate.name : ''}
                                  onChange={(_data, value) => taxRate.name = value}
                                  columnWidth={5}
                                />
                              )}
                              <EditableField<IPreferences, number>
                                testId={`settings-tax-rate-${index + 1}-field`}
                                inputType={EditableFieldTypes.percentage}
                                label={t("settings.taxIndexRate", { count: index + 1 })}
                                onProvide={() => taxRate.rate ? taxRate.rate : 0}
                                onChange={(_data, value) => taxRate.rate = value}
                                columnWidth={5}
                              />
                              {index !== 0 && <EditableField
                                inputType={EditableFieldTypes.icon}
                                label={t("general.delete")}
                                onClick={() => removeTaxRateRow(index)}
                                columnWidth={2}
                              >
                                <FiTrash></FiTrash>
                              </EditableField>}
                            </Fragment>
                          })}

                          {deLinkTaxNameAndTaxNumber &&
                            <EditableField<IPreferences, string>
                              testId="settings-tax-number-name-field"
                              inputType={EditableFieldTypes.text}
                              label={t("settings.taxNumberName")}
                              onProvide={(data) => data.company_taxName}
                              onChange={(data, value) => data.company_taxName = value}
                            />
                          }


                          {taxRates.length < 2 && <EditableField
                            inputType={EditableFieldTypes.addLabel}
                            label={t("settings.addTaxRate")}
                            onClick={addTaxRate}
                          />}

                        </Grid>
                      </EditableGroup>
                    </NotchedCard>
                  </Grid>}
                  <Grid item xs={12}>
                    <NotchedCard label={t("settings.overstays")} style={styles.card}>
                      <EditableGroup<IPreferences>
                        id="overstaySettings"
                        testId="settings-overstays-group"
                        editType={EditableGroupType.switchableEditing}
                        data={session.current.site.preferences}
                        onDataChanged={(data) => savePrefs({
                          overstays_early_arrival_tolerance_hours: data.overstays_early_arrival_tolerance_hours,
                          overstays_late_departure_tolerance_hours: data.overstays_late_departure_tolerance_hours,
                          overstays_tolerance_midnight: data.overstays_tolerance_midnight,
                        })}
                      >
                        <Grid container>
                          <EditableField<IPreferences, number>
                            testId="settings-early-arrival-tolerance-field"
                            inputType={EditableFieldTypes.number}
                            label={t("settings.earlyArrivalTolerance")}
                            onProvide={(data) => data.overstays_early_arrival_tolerance_hours}
                            onChange={(data, value) => data.overstays_early_arrival_tolerance_hours = value}
                          />
                          <EditableField<IPreferences, number>
                            testId="settings-late-departure-tolerance-field"
                            inputType={EditableFieldTypes.number}
                            label={t("settings.lateDepartureTolerance")}
                            onProvide={(data) => data.overstays_late_departure_tolerance_hours}
                            onChange={(data, value) => data.overstays_late_departure_tolerance_hours = value}
                          />
                          <EditableField<IPreferences, boolean>
                            testId="settings-overstay-midnight-switch"
                            inputType={EditableFieldTypes.switch}
                            label={t("settings.overstayMidnight")}
                            onProvide={(data) => {
                              return data.overstays_tolerance_midnight == true;
                            }}
                            onChange={(data, value) => data.overstays_tolerance_midnight = value == true}
                          />
                        </Grid>
                      </EditableGroup>
                    </NotchedCard>
                  </Grid>
                  <ProductCatalogueSetting onSave={savePrefs} />
                </Grid>
              </TabbedViewPage>

              <TabbedViewPage title={t("settings.labels")}>
                <Grid container>
                  <Grid item xs={12}>
                    <NotchedCard label={t("settings.labelPrinter")} style={styles.card}>
                      <EditableGroup<IPreferences>
                        id="browser"
                        testId="settings-label-printer-group"

                        editType={EditableGroupType.switchableEditing}
                        data={session.current.site.preferences}
                        onDataChanged={async () => {
                          return true;
                        }}
                      >
                        <LabelPrinterSetting></LabelPrinterSetting>
                      </EditableGroup>
                    </NotchedCard>
                  </Grid>
                  <Grid item xs={12}>

                    <NotchedCard label={t("settings.barrierToken")} style={styles.card}>
                      <EditableGroup<IPreferences>
                        id="browser"
                        testId="settings-barrier-token-group"

                        editType={EditableGroupType.alwaysEditing}
                        data={session.current.site.preferences}
                        onDataChanged={async () => {
                          return true;
                        }}
                      >
                        <BarrierTokenPrinter />
                      </EditableGroup>
                    </NotchedCard>
                  </Grid>

                  {isSysAdmin(session) && (
                    <Grid item xs={12}>
                      <NotchedCard label={t("settings.labelDesigner")} style={styles.card}>
                        <PrinterLabelEditor></PrinterLabelEditor>
                      </NotchedCard>
                    </Grid>
                  )}

                </Grid>
              </TabbedViewPage >

              <TabbedViewPage title={t("settings.users")}>
                {isRbacReleased ? <UserSettingsManager /> : <UserManagement />}
              </TabbedViewPage >


              <TabbedViewPage visible={canManageProducts && (((isRbacReleased ? hasPermission("bookingManagement.saleItems.read") : isAdmin(session)) && session.current.site.preferences.booking_products_enabled == true) || isSysAdmin(session))} title={t("settings.products")}>
                <ProductManager />
              </TabbedViewPage>

              <TabbedViewPage visible={canManagePackages && (((isRbacReleased ? hasPermission("bookingManagement.saleItems.read") : isAdmin(session)) && session.current.site.preferences.booking_packages_enabled == true) || isSysAdmin(session))} title={t("settings.packages")}>
                <PackageManager />
              </TabbedViewPage>

              <TabbedViewPage
                visible={
                  canManageUpsells &&
                  ((isRbacReleased ? hasPermission("bookingManagement.upsells.read") : isAdmin(session)) &&
                    session.current.site.preferences
                      .booking_upsells_enabled == true)
                }
                title={t("settings.upsells")}
              >
                <UpsellsManager />
              </TabbedViewPage>

              <TabbedViewPage visible={isSysAdmin(session)} title={t("settings.ingestion")}>
                <IngestionView></IngestionView>
              </TabbedViewPage>

              <TabbedViewPage visible={isSysAdmin(session)} title={t("settings.devices")}>
                <DeviceManagerView></DeviceManagerView>
              </TabbedViewPage>
              <TabbedViewPage visible={isAdmin(session) && (session.current.site.preferences
                .bus_walkaround_enabled == true || session.current.site.preferences
                  .wmb_enabled == true)} title={t("settings.buses")}>
                <BusManager></BusManager>
              </TabbedViewPage>

            </TabbedView>
          </div>
        </div>

      </div>
    </Fragment>
  </DashPage >);
};

export default SettingsPage;
